// @ts-nocheck
'use strict';

const initializedRoots = new WeakSet();
const mealIds = ['breakfast', 'lunch', 'dinner', 'snacks'];
const requestDelayMs = 300;
// The backend resolves all clarifications with sensible defaults once
// clarification_round >= 2, so the client never needs to resubmit beyond that.
const maxClarificationRounds = 2;

const servingEstimates = [
  { pattern: /\b(small\s+bowl)\b/i, phrase: 'small bowl', grams: 180, confidence: 'medium' },
  { pattern: /\b(large\s+bowl)\b/i, phrase: 'large bowl', grams: 350, confidence: 'medium' },
  { pattern: /\b(bowl)\b/i, phrase: 'bowl', grams: 250, confidence: 'medium' },
  { pattern: /\b(half\s+cup)\b/i, phrase: 'half cup', grams: 120, confidence: 'high' },
  { pattern: /\b(cup)\b/i, phrase: 'cup', grams: 240, confidence: 'medium' },
  { pattern: /\b(tablespoon|tbsp)\b/i, phrase: 'tablespoon', grams: 16, confidence: 'high' },
  { pattern: /\b(teaspoon|tsp)\b/i, phrase: 'teaspoon', grams: 5, confidence: 'high' },
  { pattern: /\b(slice|piece)\b/i, phrase: 'piece/slice', grams: 40, confidence: 'medium' },
  { pattern: /\b(handful)\b/i, phrase: 'handful', grams: 30, confidence: 'medium' },
  { pattern: /\b(scoop)\b/i, phrase: 'scoop', grams: 35, confidence: 'medium' },
  { pattern: /\b(plate)\b/i, phrase: 'plate', grams: 400, confidence: 'low' },
  { pattern: /\b(serving)\b/i, phrase: 'serving', grams: 120, confidence: 'low' }
];

const foodSpecificEstimates = [
  { pattern: /\b(oatmeal|porridge)\b/i, phrase: 'typical bowl', grams: 180, confidence: 'medium' },
  { pattern: /\b(toast|bread)\b/i, phrase: 'slice', grams: 40, confidence: 'medium' },
  { pattern: /\b(peanut\s+butter|almond\s+butter)\b/i, phrase: 'tablespoon', grams: 16, confidence: 'medium' },
  { pattern: /\b(coffee|tea)\b/i, phrase: 'cup', grams: 240, confidence: 'medium' },
  { pattern: /\b(smoothie|shake)\b/i, phrase: 'glass', grams: 300, confidence: 'medium' },
  { pattern: /\b(banana)\b/i, phrase: 'medium banana', grams: 118, confidence: 'medium' },
  { pattern: /\b(apple)\b/i, phrase: 'medium apple', grams: 182, confidence: 'medium' },
  { pattern: /\b(egg)\b/i, phrase: 'large egg', grams: 50, confidence: 'medium' },
  { pattern: /\b(chicken|beef|salmon|fish|pork|turkey)\b/i, phrase: 'serving', grams: 120, confidence: 'low' }
];

const foodMatchStopWords = new Set([
  'a',
  'an',
  'and',
  'with',
  'of',
  'the',
  'small',
  'large',
  'bowl',
  'cup',
  'piece',
  'slice',
  'serving',
  'plate',
  'handful',
  'scoop'
]);

const nutrients = {
  vitaminC: { id: 1162, name: 'Vitamin C', unit: 'mg', rda: 90, group: 'Vitamins' },
  vitaminA: { id: 1106, name: 'Vitamin A', unit: 'mcg', rda: 900, group: 'Vitamins' },
  vitaminD: { id: 1110, name: 'Vitamin D', unit: 'mcg', rda: 15, group: 'Vitamins' },
  vitaminE: { id: 1109, name: 'Vitamin E', unit: 'mg', rda: 15, group: 'Vitamins' },
  vitaminK: { id: 1185, name: 'Vitamin K', unit: 'mcg', rda: 120, group: 'Vitamins' },
  thiamin: { id: 1165, name: 'Thiamin (B1)', unit: 'mg', rda: 1.2, group: 'B Vitamins' },
  riboflavin: { id: 1166, name: 'Riboflavin (B2)', unit: 'mg', rda: 1.3, group: 'B Vitamins' },
  niacin: { id: 1167, name: 'Niacin (B3)', unit: 'mg', rda: 16, group: 'B Vitamins' },
  pantoB5: { id: 1186, name: 'Pantothenic Acid (B5)', unit: 'mg', rda: 5, group: 'B Vitamins' },
  vitaminB6: { id: 1175, name: 'Vitamin B6', unit: 'mg', rda: 1.7, group: 'B Vitamins' },
  vitaminB12: { id: 1178, name: 'Vitamin B12', unit: 'mcg', rda: 2.4, group: 'B Vitamins' },
  folate: { id: 1177, name: 'Folate', unit: 'mcg', rda: 400, group: 'B Vitamins' },
  calcium: { id: 1087, name: 'Calcium', unit: 'mg', rda: 1000, group: 'Minerals' },
  iron: { id: 1089, name: 'Iron', unit: 'mg', rda: 8, group: 'Minerals' },
  magnesium: { id: 1090, name: 'Magnesium', unit: 'mg', rda: 420, group: 'Minerals' },
  phosphorus: { id: 1091, name: 'Phosphorus', unit: 'mg', rda: 700, group: 'Minerals' },
  potassium: { id: 1092, name: 'Potassium', unit: 'mg', rda: 3400, group: 'Minerals' },
  selenium: { id: 1103, name: 'Selenium', unit: 'mcg', rda: 55, group: 'Minerals' },
  zinc: { id: 1095, name: 'Zinc', unit: 'mg', rda: 11, group: 'Minerals' },
  fiber: { id: 1079, name: 'Fiber', unit: 'g', rda: 28, group: 'Other' }
};

const macros = {
  calories: { id: 1008, name: 'Calories', unit: 'kcal', target: 2000 },
  protein: { id: 1003, name: 'Protein', unit: 'g', target: 50 },
  carbohydrates: { id: 1005, name: 'Carbohydrates', unit: 'g', target: 275 },
  fat: { id: 1004, name: 'Total Fat', unit: 'g', target: 78 },
  fiber: { id: 1079, name: 'Fiber', unit: 'g', target: 28 }
};

// CORE7 benefit areas, ingredient groups, and per-serving nutrient amounts are
// sourced entirely from Shopify metaobjects, injected via the [data-nutrition-copy]
// script (benefitOrder, benefits, beyond, core7Nutrients) and read by
// buildBenefits / buildBeyond / buildCore7Nutrients below.

const statusLevels = [
  { max: 25, label: 'Critical', color: '#C0392B' },
  { max: 50, label: 'Low', color: '#E67E22' },
  { max: 75, label: 'Fair', color: '#D4AC0D' },
  { max: 100, label: 'Good', color: '#27AE60' },
  { max: Infinity, label: 'Optimal', color: '#16A085' }
];

const loadedScripts = new Map();
function loadScriptOnce(src) {
  if (!src) return Promise.reject(new Error('Missing script URL'));
  if (loadedScripts.has(src)) return loadedScripts.get(src);
  const promise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
  loadedScripts.set(src, promise);
  return promise;
}

function initNutritionCheckers() {
  document.querySelectorAll('[data-nutrition-checker]').forEach((root) => {
    if (initializedRoots.has(root)) return;
    initializedRoots.add(root);
    new NutritionChecker(root);
  });
}

class NutritionChecker {
  constructor(root) {
    this.root = root;
    this.copy = readCopy(root);
    this.macroDefinitions = mergeDefinitionLabels(macros, this.copy.macros);
    this.coreBenefits = buildBenefits(this.copy);
    this.beyondNutrition = buildBeyond(this.copy);
    this.core7Nutrients = buildCore7Nutrients(this.copy);
    this.core7MonthlyPrice = parseMoneyValue(copyValue(this.copy, 'messages.core7MonthlyPrice', '0'));
    this.editorStateKey = this.root.dataset.sectionId
      ? `nutrition-checker:${this.root.dataset.sectionId}:editor-state`
      : '';
    const persistedEditorState = this.readEditorState();
    this.analysisMode = persistedEditorState?.mode === 'full' ? 'full' : 'quick';
    this.currentStage = 'mode';
    this.lastReport = null;
    this.pendingFullStackMeals = null;
    this.pendingFullStackFoods = [];
    // Per-meal clarification state. The backend is stateless and only remembers
    // prior answers via the clarifications array we send, so we accumulate every
    // answer here (keyed by question id) and track which clarification round we
    // are on so the backend can converge.
    this.clarificationsByMeal = {};
    this.clarificationRoundByMeal = {};
    this.loadingProgressFrame = null;
    this.loadingStatusTimer = null;
    this.loadingStepIndex = 0;
    this.initCards();
    this.bindEvents();
    this.updateCreateButtonStates();
    this.updateSupplementCreateButtonState();
    this.updateInputMode();
    this.renderTrackedNutrients();
    const hasPrefilledMeals = this.prefillMealTextFromUrl();
    const persistedStage = this.validRestorableStage(persistedEditorState?.stage)
      ? persistedEditorState.stage
      : 'mode';
    this.setStage(hasPrefilledMeals ? 'input' : persistedStage, { persist: false });

    this.devPreview = this.root.dataset.nutritionDevPreview === 'true';
    if (this.devPreview) {
      this.initDevPreview();
    }
  }

  get nutritionApiBase() {
    return this.root.dataset.nutritionApiBase.trim().replace(/\/$/, '');
  }

  async apiPost(path, body) {
    if (!this.nutritionApiBase) {
      throw new Error(copyValue(this.copy, 'messages.apiNotConfigured', 'Nutrition API is not configured.'));
    }

    const url = `${this.nutritionApiBase}${path}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    const data = await this.readJsonResponse(response, url);
    if (response.status === 503 && data.status === 'service_unavailable') {
      throw new Error(data.message || copyValue(this.copy, 'messages.apiUnavailable', 'Nutrition service is unavailable.'));
    }
    if (!response.ok) {
      throw new Error(data.message || `Nutrition API error: ${response.status}`);
    }
    return data;
  }

  async apiGet(path, params) {
    if (!this.nutritionApiBase) {
      throw new Error(copyValue(this.copy, 'messages.apiNotConfigured', 'Nutrition API is not configured.'));
    }

    const url = new URL(`${this.nutritionApiBase}${path}`, window.location.origin);
    Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { Accept: 'application/json' }
    });

    const data = await this.readJsonResponse(response, url.toString());
    if (response.status === 503 && data.status === 'service_unavailable') {
      throw new Error(data.message || copyValue(this.copy, 'messages.apiUnavailable', 'Nutrition service is unavailable.'));
    }
    if (!response.ok) {
      throw new Error(data.message || `Nutrition API error: ${response.status}`);
    }
    return data;
  }

  async readJsonResponse(response, url) {
    const text = await response.text();
    try {
      return text ? JSON.parse(text) : {};
    } catch (error) {
      const preview = text.replace(/\s+/g, ' ').trim().slice(0, 180);
      throw new Error(`Nutrition API returned non-JSON response from ${url} (${response.status}). ${preview || error.message}`);
    }
  }

  query(selector) {
    const element = this.root.querySelector(selector);
    if (!element) throw new Error(`Nutrition checker element missing: ${selector}`);
    return element;
  }

  initCards() {
    mealIds.forEach((mealId) => {
      const container = this.query(`[data-food-cards="${mealId}"]`);
      container.innerHTML = '';
    });
    this.query('[data-supplement-cards]').innerHTML = '';
  }

  createFoodCard(mealId, item) {
    const foodConfidenceValue = item.foodConfidence || 'medium';
    const servingConfidenceValue = item.servingConfidence || item.confidence || 'low';
    const card = document.createElement('div');
    card.className = 'nutrition-checker__food-card';
    card.dataset.foodCard = mealId;
    card.dataset.confidence = servingConfidenceValue;
    card.dataset.foodConfidence = foodConfidenceValue;
    card.dataset.servingConfidence = servingConfidenceValue;
    card.dataset.foodStatus = foodConfidenceValue === 'high' ? 'matched' : 'pending';
    card.dataset.fdcId = '';

    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.name = `${mealId}_food_name`;
    nameInput.value = item.name;
    nameInput.placeholder = copyValue(this.copy, 'placeholders.foodName', 'Food name');
    nameInput.autocomplete = 'off';
    nameInput.dataset.foodName = '';

    const servingInput = document.createElement('input');
    servingInput.type = 'text';
    servingInput.name = `${mealId}_serving_phrase`;
    servingInput.value = item.servingPhrase;
    servingInput.placeholder = copyValue(this.copy, 'placeholders.servingPhrase', 'Serving description');
    servingInput.autocomplete = 'off';
    servingInput.dataset.servingPhrase = '';

    const gramsInput = document.createElement('input');
    gramsInput.type = 'number';
    gramsInput.name = `${mealId}_serving_grams`;
    gramsInput.value = String(item.servingGrams);
    gramsInput.min = '1';
    gramsInput.step = '1';
    gramsInput.placeholder = copyValue(this.copy, 'placeholders.servingGrams', 'grams');
    gramsInput.dataset.servingGrams = '';

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'nutrition-checker__remove-button';
    removeButton.textContent = 'x';
    removeButton.setAttribute('aria-label', copyValue(this.copy, 'placeholders.removeRow', 'Remove food card'));
    removeButton.addEventListener('click', () => card.remove());

    const nameLabel = this.createCardField(copyValue(this.copy, 'messages.cardFoodLabel', 'Food'), nameInput);
    const servingLabel = this.createCardField(copyValue(this.copy, 'messages.cardServingLabel', 'Estimated serving'), servingInput);
    const gramsLabel = this.createCardField(copyValue(this.copy, 'messages.cardGramsLabel', 'Grams'), gramsInput);
    const grid = document.createElement('div');
    grid.className = 'nutrition-checker__card-grid';
    grid.append(nameLabel, servingLabel, gramsLabel, removeButton);

    const foodConfidence = document.createElement('span');
    foodConfidence.className = 'nutrition-checker__confidence';
    foodConfidence.textContent = `${copyValue(this.copy, 'messages.foodConfidenceLabel', 'Food confidence')}: ${this.confidenceLabel(foodConfidenceValue)}`;

    const servingConfidence = document.createElement('span');
    servingConfidence.className = 'nutrition-checker__confidence';
    servingConfidence.textContent = `${copyValue(this.copy, 'messages.servingConfidenceLabel', 'Serving confidence')}: ${this.confidenceLabel(servingConfidenceValue)}`;

    const confidenceGroup = document.createElement('div');
    confidenceGroup.className = 'nutrition-checker__confidence-group';
    confidenceGroup.append(foodConfidence, servingConfidence);

    const message = document.createElement('div');
    message.className = 'nutrition-checker__card-message';
    message.dataset.cardMessage = '';
    nameInput.addEventListener('input', () => {
      message.innerHTML = '';
      card.dataset.foodStatus = 'pending';
      card.dataset.fdcId = '';
    });

    card.append(grid, confidenceGroup);
    if (item.notes && servingConfidenceValue !== 'high') {
      const note = document.createElement('p');
      note.className = 'nutrition-checker__confidence-note';
      note.textContent = item.notes;
      card.append(note);
    }
    card.append(message);
    return card;
  }

  createCardField(labelText, input) {
    const label = document.createElement('label');
    label.className = 'nutrition-checker__card-field';
    const span = document.createElement('span');
    span.textContent = labelText;
    label.append(span, input);
    return label;
  }

  createSupplementCard(item) {
    const card = document.createElement('div');
    card.className = 'nutrition-checker__supplement-card';
    card.dataset.supplementCard = '';
    card.dataset.supplementStatus = item.label_id ? 'matched' : 'pending';
    card.dataset.labelId = item.label_id || '';
    card.dataset.confidence = item.confidence || 'medium';
    card.dataset.dataSource = item.label_id ? 'dsld' : '';
    card.dataset.originalText = item.original_text || item.name || '';

    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.name = 'supplement_product_name';
    nameInput.value = item.name || '';
    nameInput.placeholder = copyValue(this.copy, 'placeholders.supplementName', 'Supplement product');
    nameInput.autocomplete = 'off';
    nameInput.dataset.supplementName = '';

    const brandInput = document.createElement('input');
    brandInput.type = 'text';
    brandInput.name = 'supplement_brand_name';
    brandInput.value = item.brand || '';
    brandInput.placeholder = copyValue(this.copy, 'placeholders.supplementBrand', 'Brand');
    brandInput.autocomplete = 'off';
    brandInput.dataset.supplementBrand = '';

    const servingInput = document.createElement('input');
    servingInput.type = 'number';
    servingInput.name = 'supplement_serving_quantity';
    servingInput.value = String(item.serving_quantity || 1);
    servingInput.min = '0';
    servingInput.step = '0.5';
    servingInput.placeholder = copyValue(this.copy, 'placeholders.supplementServing', 'Serving');
    servingInput.dataset.supplementServing = '';

    const unitInput = document.createElement('input');
    unitInput.type = 'text';
    unitInput.name = 'supplement_serving_unit';
    unitInput.value = item.serving_unit || '';
    unitInput.placeholder = copyValue(this.copy, 'placeholders.supplementUnit', 'Unit');
    unitInput.autocomplete = 'off';
    unitInput.dataset.supplementUnit = '';

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'nutrition-checker__remove-button';
    removeButton.textContent = 'x';
    removeButton.setAttribute('aria-label', copyValue(this.copy, 'placeholders.removeRow', 'Remove food card'));
    removeButton.addEventListener('click', () => card.remove());

    const grid = document.createElement('div');
    grid.className = 'nutrition-checker__supplement-card-grid';
    grid.append(
      this.createCardField(copyValue(this.copy, 'messages.supplementProductLabel', 'Product'), nameInput),
      this.createCardField(copyValue(this.copy, 'messages.supplementBrandLabel', 'Brand'), brandInput),
      this.createCardField(copyValue(this.copy, 'messages.supplementServingLabel', 'Daily serving'), servingInput),
      this.createCardField(copyValue(this.copy, 'messages.supplementUnitLabel', 'Unit'), unitInput),
      removeButton
    );

    const confidence = document.createElement('span');
    confidence.className = 'nutrition-checker__confidence';
    confidence.setAttribute('data-supplement-match-label', '');

    const message = document.createElement('div');
    message.className = 'nutrition-checker__card-message';
    message.dataset.supplementMessage = '';

    [nameInput, brandInput].forEach((input) => {
      input.addEventListener('input', () => {
        card.dataset.supplementStatus = 'pending';
        card.dataset.labelId = '';
        card.dataset.dataSource = '';
        card.dataset.confidence = 'low';
        this.updateSupplementConfidenceBadge(card);
        message.innerHTML = '';
      });
    });

    card.append(grid, confidence);
    if (item.notes) {
      const note = document.createElement('small');
      note.textContent = item.notes;
      card.append(note);
    }
    card.append(message);
    this.updateSupplementConfidenceBadge(card, item.match_label ? { matchedLabel: item.match_label } : {});
    return card;
  }

  supplementConfidenceLabel(confidence) {
    if (confidence === 'high') {
      return copyValue(this.copy, 'messages.supplementConfidenceHigh', 'Strong match');
    }
    if (confidence === 'medium') {
      return copyValue(this.copy, 'messages.supplementConfidenceMedium', 'Possible match — please confirm');
    }
    return copyValue(this.copy, 'messages.supplementConfidenceLow', 'Weak match');
  }

  supplementSuggestionLabel(suggestion) {
    const fullName = String(suggestion.full_name || '').trim();
    const brandName = String(suggestion.brand_name || '').trim();
    if (!fullName) return brandName;
    if (!brandName) return fullName;
    if (fullName.toLowerCase().startsWith(brandName.toLowerCase())) return fullName;
    return `${brandName} ${fullName}`;
  }

  manualSupplementDetails(card) {
    const servingQuantity = Number.parseFloat(card.querySelector('[data-supplement-serving]').value);
    return {
      name: card.querySelector('[data-supplement-name]').value.trim(),
      brand: card.querySelector('[data-supplement-brand]').value.trim(),
      original_text: card.dataset.originalText || '',
      serving_quantity: Number.isFinite(servingQuantity) && servingQuantity > 0 ? servingQuantity : 0,
      serving_unit: card.querySelector('[data-supplement-unit]').value.trim(),
      confidence: card.dataset.confidence || 'medium',
      data_source: 'user_provided'
    };
  }

  supplementHasManualDetails(card) {
    const details = this.manualSupplementDetails(card);
    return details.name.length > 0 && details.serving_quantity > 0 && details.serving_unit.length > 0;
  }

  updateSupplementConfidenceBadge(card, options = {}) {
    const badge = card.querySelector('[data-supplement-match-label]');
    if (!badge) return;

    const confidence = card.dataset.confidence || 'low';
    const isMatched = Boolean(card.dataset.labelId);
    const isManual = card.dataset.dataSource === 'user_provided';
    const matchedLabel = options.matchedLabel
      || (isMatched ? card.querySelector('[data-supplement-name]').value.trim() : '');

    badge.classList.remove(
      'nutrition-checker__confidence--high',
      'nutrition-checker__confidence--medium',
      'nutrition-checker__confidence--low'
    );

    if (isMatched && matchedLabel) {
      badge.classList.add('nutrition-checker__confidence--high');
      badge.textContent = `${copyValue(this.copy, 'messages.supplementMatchLabel', 'DSLD match')}: ${matchedLabel}`;
      return;
    }

    if (isManual) {
      badge.classList.add('nutrition-checker__confidence--medium');
      badge.textContent = copyValue(this.copy, 'messages.userProvidedEstimate', 'User-provided estimate');
      return;
    }

    badge.classList.add(`nutrition-checker__confidence--${confidence}`);
    badge.textContent = this.supplementConfidenceLabel(confidence);
  }

  bindEvents() {
    this.root.querySelectorAll('[data-select-analysis-mode]').forEach((button) => {
      button.addEventListener('click', () => {
        if (button.dataset.selectAnalysisMode === 'quick') {
          this.analysisMode = 'quick';
          this.updateInputMode();
          this.setStage('input');
          this.query('[data-nutrition-stage="input"]').scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }

        this.analysisMode = 'full';
        this.updateInputMode();
        this.setStage('input');
        this.query('[data-nutrition-stage="input"]').scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    this.root.querySelectorAll('[data-back-to-analysis-mode]').forEach((button) => {
      button.addEventListener('click', () => {
        this.setStage('mode');
        this.query('[data-nutrition-stage="mode"]').scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    this.query('[data-create-supplement-cards]').addEventListener('click', () => {
      void this.createSupplementCards();
    });

    this.query('[data-supplement-text]').addEventListener('input', () => {
      this.updateSupplementCreateButtonState();
    });

    this.query('[data-clear-supplements]').addEventListener('click', () => {
      this.query('[data-supplement-text]').value = '';
      this.query('[data-supplement-cards]').innerHTML = '';
      this.updateSupplementCreateButtonState();
    });

    this.query('[data-skip-supplements]').addEventListener('click', () => {
      this.query('[data-supplement-text]').value = '';
      this.query('[data-supplement-cards]').innerHTML = '';
      this.updateSupplementCreateButtonState();
      void this.analyzeFullStack(true);
    });

    this.root.querySelectorAll('[data-create-cards]').forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        // A fresh "Create food cards" click starts a new parse, so drop any prior answers.
        this.resetClarificationState(button.dataset.createCards);
        void this.createCardsForMeal(button.dataset.createCards);
      });
    });

    this.root.querySelectorAll('[data-meal-text]').forEach((textarea) => {
      textarea.addEventListener('input', () => {
        // Editing the meal text invalidates any answers gathered for the old text.
        this.resetClarificationState(textarea.dataset.mealText);
        this.updateCreateButtonState(textarea.dataset.mealText);
      });
    });

    this.root.querySelectorAll('[data-clear-meal]').forEach((button) => {
      button.addEventListener('click', () => {
        const mealId = button.dataset.clearMeal;
        this.query(`[data-meal-text="${mealId}"]`).value = '';
        this.query(`[data-food-cards="${mealId}"]`).innerHTML = '';
        this.updateCreateButtonState(mealId);
      });
    });

    this.query('[data-analyze]').addEventListener('click', () => this.handleDietSubmit());
    this.query('[data-analyze-full-stack]').addEventListener('click', () => this.analyzeFullStack(false));
    this.query('[data-try-again]').addEventListener('click', () => this.reset());
    this.query('[data-download-report]').addEventListener('click', () => this.downloadReportPdf());
    this.query('[data-open-nutrients]').addEventListener('click', () => this.openDialog('[data-nutrients-dialog]'));
    this.query('[data-close-nutrients]').addEventListener('click', () => this.closeDialog('[data-nutrients-dialog]'));
  }

  printReport() {
    const results = this.query('[data-nutrition-stage="results"]');
    const disclosures = Array.from(results.querySelectorAll('details'));
    const closedDisclosures = disclosures.filter((disclosure) => !disclosure.open);
    let restored = false;

    closedDisclosures.forEach((disclosure) => {
      disclosure.open = true;
    });
    document.body.classList.add('nutrition-checker-report-printing');

    const restoreReport = () => {
      if (restored) return;
      restored = true;
      document.body.classList.remove('nutrition-checker-report-printing');
      closedDisclosures.forEach((disclosure) => {
        disclosure.open = false;
      });
      window.removeEventListener('afterprint', restoreReport);
    };

    window.addEventListener('afterprint', restoreReport, { once: true });
    window.requestAnimationFrame(() => {
      window.print();
      window.setTimeout(restoreReport, 1500);
    });
  }

  ensurePdfLibraries() {
    if (this._pdfLibrariesPromise) return this._pdfLibrariesPromise;
    const { jspdfUrl, autotableUrl, reportPdfUrl } = this.root.dataset;
    this._pdfLibrariesPromise = (async () => {
      if (!window.jspdf || !window.jspdf.jsPDF) {
        await loadScriptOnce(jspdfUrl);
      }
      // jspdf-autotable registers itself onto the loaded jsPDF, so it must
      // load after jsPDF is present.
      await loadScriptOnce(autotableUrl);
      if (!window.Core7NutritionReportPdf) {
        await loadScriptOnce(reportPdfUrl);
      }
    })().catch((error) => {
      // Allow a later retry if the first attempt fails (e.g. offline).
      this._pdfLibrariesPromise = null;
      throw error;
    });
    return this._pdfLibrariesPromise;
  }

  async downloadReportPdf() {
    const button = this.query('[data-download-report]');
    const originalLabel = button ? button.textContent : '';

    try {
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = button.dataset.loadingLabel || 'Preparing PDF…';
      }

      await this.ensurePdfLibraries();
      if (!window.Core7NutritionReportPdf) {
        throw new Error('PDF generator unavailable');
      }
      // The generator reads the report content directly from the DOM, which
      // includes collapsed disclosures and "Show all" ingredient lists (they
      // are present in the DOM, only hidden via CSS), so nothing needs to be
      // expanded first.
      window.Core7NutritionReportPdf.generate(this.root, {
        filename: 'CORE7-Nutrition-Report.pdf'
      });
    } catch (error) {
      console.error('Nutrition report PDF generation failed; falling back to print.', error);
      this.printReport();
    } finally {
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.textContent = originalLabel;
      }
    }
  }

  updateCreateButtonStates() {
    mealIds.forEach((mealId) => this.updateCreateButtonState(mealId));
  }

  updateInputMode() {
    const analyzeButton = this.query('[data-analyze]');
    analyzeButton.textContent = this.analysisMode === 'full'
      ? copyValue(this.copy, 'messages.fullStackContinueLabel', 'Continue to Supplements')
      : copyValue(this.copy, 'messages.quickAnalyzeLabel', 'Analyze My Nutrition');
  }

  updateCreateButtonState(mealId) {
    const button = this.query(`[data-create-cards="${mealId}"]`);
    const textarea = this.query(`[data-meal-text="${mealId}"]`);
    button.disabled = textarea.value.trim().length === 0;
  }

  updateSupplementCreateButtonState() {
    const button = this.query('[data-create-supplement-cards]');
    const textarea = this.query('[data-supplement-text]');
    button.disabled = textarea.value.trim().length === 0;
  }

  getMeals() {
    const meals = {};
    mealIds.forEach((mealId) => {
      this.ensureMealCards(mealId);
      const cards = this.query(`[data-food-cards="${mealId}"]`).querySelectorAll('[data-food-card]');
      meals[mealId] = Array.from(cards).map((card) => {
        const name = card.querySelector('[data-food-name]').value.trim();
        const servingPhrase = card.querySelector('[data-serving-phrase]').value.trim();
        const servingGrams = Number.parseInt(card.querySelector('[data-serving-grams]').value, 10);
        return {
          meal: mealId,
          name,
          servingPhrase,
          servingGrams: Number.isFinite(servingGrams) && servingGrams > 0 ? servingGrams : estimateServing(name).grams,
          confidence: card.dataset.servingConfidence || card.dataset.confidence || 'low',
          food_confidence: card.dataset.foodConfidence,
          serving_confidence: card.dataset.servingConfidence || card.dataset.confidence || 'low',
          fdcId: card.dataset.foodStatus === 'matched' ? parseFdcId(card.dataset.fdcId) : null
        };
      }).filter((item) => item.name.length > 0);
    });
    return meals;
  }

  resetClarificationState(mealId) {
    this.clarificationsByMeal[mealId] = {};
    this.clarificationRoundByMeal[mealId] = 0;
  }

  async createCardsForMeal(mealId) {
    const textarea = this.query(`[data-meal-text="${mealId}"]`);
    const container = this.query(`[data-food-cards="${mealId}"]`);
    const button = this.query(`[data-create-cards="${mealId}"]`);
    const mealText = textarea.value.trim();
    container.innerHTML = '';
    this.clearFoodValidationMessages();

    if (!mealText) return;

    button.disabled = true;
    const loading = document.createElement('p');
    loading.className = 'nutrition-checker__disclaimer';
    loading.textContent = copyValue(this.copy, 'messages.parsingMeals', 'Analyzing your meal description...');
    container.append(loading);

    try {
      // Send the raw text unchanged plus the FULL accumulated answer set and the
      // current round — the backend re-parses from scratch and relies on these.
      const clarifications = Object.values(this.clarificationsByMeal[mealId] || {});
      const payload = {
        meal: mealId,
        text: mealText,
        clarification_round: this.clarificationRoundByMeal[mealId] || 0
      };
      if (clarifications.length > 0) payload.clarifications = clarifications;
      const data = await this.apiPost('/parse-meals', payload);
      container.innerHTML = '';

      const hasFoods = Array.isArray(data.foods) && data.foods.length > 0;
      const hasQuestions = Array.isArray(data.questions) && data.questions.length > 0;
      if (!['ok', 'needs_clarification'].includes(data.status) || (!hasFoods && !hasQuestions)) {
        const help = document.createElement('p');
        help.className = 'nutrition-checker__disclaimer';
        help.textContent = copyValue(this.copy, 'messages.cardsHelp', 'Review the foods we found. You can adjust the name or serving estimate before analysis.');
        container.append(help);
        return;
      }

      this.renderClarificationQuestions(mealId, data.questions || [], container);
      (data.foods || []).forEach((item) => {
        container.append(
          this.createFoodCard(mealId, {
            name: item.name,
            servingPhrase: item.serving_phrase,
            servingGrams: item.serving_grams,
            confidence: item.confidence,
            foodConfidence: item.food_confidence,
            servingConfidence: item.serving_confidence,
            notes: item.notes
          })
        );
      });
      await this.validateCardsForMeal(mealId);
    } catch (error) {
      container.innerHTML = '';
      const help = document.createElement('p');
      help.className = 'nutrition-checker__disclaimer';
      help.textContent = error.message;
      container.append(help);
    } finally {
      this.updateCreateButtonState(mealId);
    }
  }

  renderClarificationQuestions(mealId, questions, container) {
    if (!Array.isArray(questions) || questions.length === 0) return;

    const panel = document.createElement('div');
    panel.className = 'nutrition-checker__clarifications';
    panel.dataset.clarificationPanel = mealId;

    const heading = document.createElement('h4');
    heading.textContent = copyValue(this.copy, 'messages.clarificationHeading', 'A couple quick clarifications');
    panel.append(heading);

    const selectedAnswers = {};
    questions.forEach((question) => {
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'nutrition-checker__clarification';

      const legend = document.createElement('legend');
      legend.textContent = question.question;
      fieldset.append(legend);

      if (question.reason) {
        const reason = document.createElement('p');
        reason.className = 'nutrition-checker__clarification-reason';
        reason.textContent = question.reason;
        fieldset.append(reason);
      }

      const options = document.createElement('div');
      options.className = 'nutrition-checker__clarification-options';

      const otherInput = document.createElement('input');
      otherInput.type = 'text';
      otherInput.className = 'nutrition-checker__clarification-other-input';
      otherInput.placeholder = copyValue(this.copy, 'messages.clarificationOtherPlaceholder', 'Other — type your answer');

      // Answer = any checked option labels plus any free-text the user typed.
      // The question counts as answered if either source has a value.
      const updateAnswer = () => {
        const checkedOptions = Array.from(options.querySelectorAll('input[type="checkbox"]:checked')).map((checkedInput) => checkedInput.value);
        const freeText = otherInput.value.trim();
        const parts = freeText ? checkedOptions.concat(freeText) : checkedOptions;
        if (parts.length === 0) {
          delete selectedAnswers[question.id];
          return;
        }

        selectedAnswers[question.id] = {
          id: question.id,
          meal: question.meal,
          target_food: question.target_food,
          question: question.question,
          answer: parts.join(', '),
          reason: question.reason
        };
      };

      question.options.forEach((option) => {
        const label = document.createElement('label');
        label.className = 'nutrition-checker__clarification-option';
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.name = `${mealId}_${question.id}`;
        input.value = option;
        input.addEventListener('change', updateAnswer);
        const span = document.createElement('span');
        span.textContent = option;
        label.append(input, span);
        options.append(label);
      });

      const otherLabel = document.createElement('label');
      otherLabel.className = 'nutrition-checker__clarification-option nutrition-checker__clarification-option--other';
      const otherSpan = document.createElement('span');
      otherSpan.textContent = copyValue(this.copy, 'messages.clarificationOtherLabel', 'Other');
      otherInput.addEventListener('input', updateAnswer);
      otherLabel.append(otherSpan, otherInput);
      options.append(otherLabel);

      fieldset.append(options);
      panel.append(fieldset);
    });

    const actions = document.createElement('div');
    actions.className = 'nutrition-checker__clarification-actions';

    const apply = document.createElement('button');
    apply.type = 'button';
    apply.className = 'nutrition-checker__add-button';
    apply.textContent = copyValue(this.copy, 'messages.applyClarifications', 'Apply answers');
    apply.addEventListener('click', () => {
      const answers = Object.values(selectedAnswers);
      if (answers.length === 0) return;

      // Merge this round's answers into the accumulated set (keyed by question id,
      // so a re-answer for the same question overwrites the earlier one), then
      // advance the round so the backend can converge.
      if (!this.clarificationsByMeal[mealId]) this.clarificationsByMeal[mealId] = {};
      answers.forEach((answer) => {
        this.clarificationsByMeal[mealId][answer.id] = answer;
      });
      const nextRound = (this.clarificationRoundByMeal[mealId] || 0) + 1;
      this.clarificationRoundByMeal[mealId] = nextRound;

      // Defense-in-depth: the backend already resolves everything at round >= 2,
      // so never resubmit beyond that — the cards on screen are already the
      // resolved set and there is nothing more to ask.
      if (nextRound > maxClarificationRounds) {
        panel.remove();
        return;
      }
      void this.createCardsForMeal(mealId);
    });

    const skip = document.createElement('button');
    skip.type = 'button';
    skip.className = 'nutrition-checker__text-button';
    skip.textContent = copyValue(this.copy, 'messages.skipClarifications', 'Skip for now');
    skip.addEventListener('click', () => panel.remove());

    actions.append(apply, skip);
    panel.append(actions);
    container.append(panel);
  }

  async createSupplementCards() {
    const textarea = this.query('[data-supplement-text]');
    const container = this.query('[data-supplement-cards]');
    const button = this.query('[data-create-supplement-cards]');
    const supplementText = textarea.value.trim();
    container.innerHTML = '';

    if (!supplementText) return;

    button.disabled = true;
    const loading = document.createElement('p');
    loading.className = 'nutrition-checker__disclaimer';
    loading.textContent = copyValue(this.copy, 'messages.parsingSupplements', 'Reading your supplement list...');
    container.append(loading);

    try {
      const data = await this.apiPost('/supplements/parse', { text: supplementText });
      container.innerHTML = '';

      if (data.status !== 'ok' || !Array.isArray(data.supplements) || data.supplements.length === 0) {
        const help = document.createElement('p');
        help.className = 'nutrition-checker__disclaimer';
        help.textContent = copyValue(this.copy, 'messages.supplementCardsHelp', 'Review the supplements we found. You can edit the product, brand, serving, or match before analysis.');
        container.append(help);
        return;
      }

      data.supplements.forEach((item) => {
        container.append(this.createSupplementCard({
          name: item.name,
          brand: item.brand,
          original_text: item.original_text,
          serving_quantity: item.serving_quantity,
          serving_unit: item.serving_unit,
          label_id: item.label_id,
          confidence: item.confidence,
          notes: item.notes,
          match_label: item.match_label
        }));
      });
      await this.validateAllSupplementCards();
    } catch (error) {
      container.innerHTML = '';
      const help = document.createElement('p');
      help.className = 'nutrition-checker__disclaimer';
      help.textContent = error.message;
      container.append(help);
    } finally {
      this.updateSupplementCreateButtonState();
    }
  }

  getSupplements() {
    return Array.from(this.query('[data-supplement-cards]').querySelectorAll('[data-supplement-card]')).map((card) => {
      const servingQuantity = Number.parseFloat(card.querySelector('[data-supplement-serving]').value);
      const serving = Number.isFinite(servingQuantity) && servingQuantity > 0 ? servingQuantity : 1;

      if (card.dataset.dataSource === 'user_provided') {
        const details = this.manualSupplementDetails(card);
        return {
          name: details.name,
          brand: details.brand,
          original_text: details.original_text,
          serving_quantity: details.serving_quantity,
          serving_unit: details.serving_unit,
          confidence: details.confidence,
          data_source: 'user_provided'
        };
      }

      if (card.dataset.labelId) {
        return {
          label_id: card.dataset.labelId,
          serving_quantity: serving,
          serving_unit: card.querySelector('[data-supplement-unit]').value.trim(),
          confidence: card.dataset.confidence || 'medium'
        };
      }

      return {
        name: card.querySelector('[data-supplement-name]').value.trim(),
        brand: card.querySelector('[data-supplement-brand]').value.trim(),
        original_text: card.dataset.originalText || '',
        serving_quantity: serving,
        serving_unit: card.querySelector('[data-supplement-unit]').value.trim(),
        confidence: card.dataset.confidence || 'medium',
        data_source: 'user_provided'
      };
    }).filter((item) => {
      if (item.data_source === 'user_provided') return item.name.length > 0;
      return Boolean(item.label_id);
    });
  }

  async validateAllSupplementCards() {
    const unmatched = [];
    const cards = Array.from(this.query('[data-supplement-cards]').querySelectorAll('[data-supplement-card]'));

    for (const card of cards) {
      const valid = await this.validateSupplementCard(card);
      if (!valid) {
        unmatched.push(card);
      }
    }

    return unmatched;
  }

  async validateSupplementCard(card) {
    const name = card.querySelector('[data-supplement-name]').value.trim();
    const brand = card.querySelector('[data-supplement-brand]').value.trim();
    const message = card.querySelector('[data-supplement-message]');

    if (!name) return false;
    if (card.dataset.supplementStatus === 'matched' && card.dataset.labelId) return true;
    if (card.dataset.dataSource === 'user_provided' && this.supplementHasManualDetails(card)) return true;

    card.dataset.supplementStatus = 'checking';
    message.innerHTML = '';
    const checking = document.createElement('p');
    checking.textContent = copyTemplate(
      this.copy,
      'messages.checkingSupplement',
      { supplement: name },
      `Checking "${name}" against NIH DSLD labels...`
    );
    message.append(checking);

    try {
      const data = await this.apiGet('/supplements/search', { query: name, brand, limit: 10 });
      if (data.status !== 'ok') {
        throw new Error(copyValue(this.copy, 'messages.apiUnavailable', 'Nutrition service is unavailable.'));
      }

      const suggestions = Array.isArray(data.supplements) ? data.supplements : [];

      card.dataset.supplementStatus = 'unmatched';
      card.dataset.labelId = '';
      card.dataset.dataSource = '';
      card.dataset.confidence = 'low';
      this.updateSupplementConfidenceBadge(card);
      this.renderSupplementValidationMessage(card, {
        query: name,
        suggestions,
        bestMatch: data.best_match,
        hasBrand: brand.length > 0
      });
      return false;
    } catch (error) {
      card.dataset.supplementStatus = 'unmatched';
      card.dataset.labelId = '';
      card.dataset.dataSource = '';
      card.dataset.confidence = 'low';
      this.updateSupplementConfidenceBadge(card);
      this.renderSupplementValidationMessage(card, {
        query: name,
        suggestions: [],
        hasBrand: brand.length > 0,
        error: error.message
      });
      return false;
    }
  }

  applySupplementMatch(card, item) {
    card.dataset.supplementStatus = 'matched';
    card.dataset.labelId = String(item.label_id);
    card.dataset.dataSource = 'dsld';
    card.dataset.confidence = item.confidence || 'high';
    card.querySelector('[data-supplement-name]').value = item.full_name;
    card.querySelector('[data-supplement-brand]').value = item.brand_name;
    this.updateSupplementConfidenceBadge(card, { matchedLabel: item.full_name });
  }

  applyManualSupplementEntry(card) {
    const message = card.querySelector('[data-supplement-message]');
    if (!this.supplementHasManualDetails(card)) {
      message.innerHTML = '';
      const warning = document.createElement('p');
      warning.textContent = copyValue(
        this.copy,
        'messages.manualSupplementMissingDetails',
        'Enter the supplement name, daily quantity, and unit before using your entry.'
      );
      message.append(warning);
      return false;
    }

    card.dataset.supplementStatus = 'matched';
    card.dataset.labelId = '';
    card.dataset.dataSource = 'user_provided';
    card.dataset.confidence = card.dataset.confidence || 'medium';
    this.updateSupplementConfidenceBadge(card);
    message.innerHTML = '';
    return true;
  }

  renderSupplementValidationMessage(card, item) {
    const message = card.querySelector('[data-supplement-message]');
    message.innerHTML = '';

    const text = document.createElement('p');
    if (item.error) {
      text.textContent = item.error;
    } else if (item.hasBrand) {
      text.textContent = copyValue(
        this.copy,
        'messages.supplementNotFoundWithBrand',
        'We couldn\'t confidently match this product. Please choose the closest label below.'
      );
    } else {
      text.textContent = copyTemplate(
        this.copy,
        'messages.supplementNotFound',
        { supplement: item.query },
        `We could not confidently match "${item.query}" to a DSLD label. Try adding the brand or choosing a suggestion.`
      );
    }
    message.append(text);

    if (item.bestMatch) {
      const bestMatchButton = document.createElement('button');
      bestMatchButton.type = 'button';
      bestMatchButton.className = 'nutrition-checker__add-button';
      bestMatchButton.textContent = copyTemplate(
        this.copy,
        'messages.useSupplementSuggestion',
        { supplement: this.supplementSuggestionLabel(item.bestMatch) },
        `Use ${this.supplementSuggestionLabel(item.bestMatch)}`
      );
      bestMatchButton.addEventListener('click', () => {
        this.applySupplementMatch(card, item.bestMatch);
        message.innerHTML = '';
      });
      message.append(bestMatchButton);
    }

    if (Array.isArray(item.suggestions) && item.suggestions.length > 0) {
      const label = document.createElement('span');
      label.textContent = copyValue(this.copy, 'messages.suggestionsLabel', 'Did you mean:');
      const suggestions = document.createElement('div');
      suggestions.className = 'nutrition-checker__suggestions';
      item.suggestions.slice(0, 10).forEach((suggestion) => {
        const suggestionLabel = this.supplementSuggestionLabel(suggestion);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'nutrition-checker__suggestion-button';
        button.textContent = suggestionLabel;
        button.setAttribute(
          'aria-label',
          copyTemplate(this.copy, 'messages.useSupplementSuggestion', { supplement: suggestionLabel }, `Use ${suggestionLabel}`)
        );
        button.addEventListener('click', () => {
          this.applySupplementMatch(card, suggestion);
          message.innerHTML = '';
        });
        suggestions.append(button);
      });
      message.append(label, suggestions);
    }

    const manualButton = document.createElement('button');
    manualButton.type = 'button';
    manualButton.className = 'nutrition-checker__add-button';
    manualButton.textContent = copyValue(this.copy, 'messages.useManualSupplementEntry', 'Use my entry anyway');
    manualButton.addEventListener('click', () => {
      this.applyManualSupplementEntry(card);
    });
    message.append(manualButton);
  }

  async validateCardsForMeal(mealId) {
    const cards = Array.from(this.query(`[data-food-cards="${mealId}"]`).querySelectorAll('[data-food-card]'));

    for (let index = 0; index < cards.length; index += 1) {
      if (index > 0) await sleep(requestDelayMs);
      await this.validateFoodCard(cards[index]);
    }
  }

  async validateFoodCard(card) {
    const query = card.querySelector('[data-food-name]').value.trim();
    const message = card.querySelector('[data-card-message]');
    if (!query) return false;
    if (card.dataset.foodStatus === 'matched') return true;

    card.dataset.foodStatus = 'checking';
    message.innerHTML = '';
    const checking = document.createElement('p');
    checking.textContent = copyTemplate(
      this.copy,
      'messages.checkingFood',
      { food: query },
      `Checking "${query}" against the USDA food database...`
    );
    message.append(checking);

    try {
      const results = await this.searchFood(query);
      const match = this.selectFoodMatch(query, results);
      if (match.food) {
        const fdcId = parseFdcId(match.food.fdcId);
        card.dataset.foodStatus = 'matched';
        card.dataset.fdcId = Number.isInteger(fdcId) ? String(fdcId) : '';
        message.innerHTML = '';
        return true;
      }

      card.dataset.foodStatus = 'unmatched';
      card.dataset.fdcId = '';
      this.renderFoodValidationMessage(card, { query, suggestions: match.suggestions });
      return false;
    } catch (error) {
      card.dataset.foodStatus = 'unmatched';
      card.dataset.fdcId = '';
      this.renderFoodValidationMessage(card, { query, suggestions: [], error: error.message });
      return false;
    }
  }

  async validateAllFoodCards() {
    const unmatched = [];

    for (const mealId of mealIds) {
      const cards = Array.from(this.query(`[data-food-cards="${mealId}"]`).querySelectorAll('[data-food-card]'));
      for (const card of cards) {
        const valid = await this.validateFoodCard(card);
        if (!valid) {
          unmatched.push({ meal: mealId, query: card.querySelector('[data-food-name]').value.trim() });
        }
      }
    }

    return unmatched;
  }

  ensureMealCards(mealId) {
    const container = this.query(`[data-food-cards="${mealId}"]`);
    const textarea = this.query(`[data-meal-text="${mealId}"]`);
    if (container.querySelector('[data-food-card]') || !textarea.value.trim()) return;
    void this.createCardsForMeal(mealId);
  }

  clearFoodValidationMessages() {
    this.root.querySelectorAll('[data-card-message]').forEach((element) => {
      element.innerHTML = '';
    });
  }

  confidenceLabel(confidence) {
    if (confidence === 'high') return copyValue(this.copy, 'messages.confidenceHigh', 'High');
    if (confidence === 'medium') return copyValue(this.copy, 'messages.confidenceMedium', 'Medium');
    return copyValue(this.copy, 'messages.confidenceLow', 'Low');
  }

  flattenMeals(meals) {
    return mealIds.flatMap((mealId) => meals[mealId]);
  }

  readEditorState() {
    if (!window.Shopify?.designMode || !this.editorStateKey) return null;

    try {
      return JSON.parse(window.sessionStorage.getItem(this.editorStateKey) || 'null');
    } catch (error) {
      return null;
    }
  }

  saveEditorState() {
    if (!window.Shopify?.designMode || !this.editorStateKey) return;

    const restorableStage = this.validRestorableStage(this.currentStage) ? this.currentStage : 'mode';
    try {
      window.sessionStorage.setItem(
        this.editorStateKey,
        JSON.stringify({
          mode: this.analysisMode,
          stage: restorableStage
        })
      );
    } catch (error) {
      // Theme editor preview should keep working even if browser storage is unavailable.
    }
  }

  validRestorableStage(stage) {
    return ['mode', 'input', 'supplements'].includes(stage);
  }

  setStage(stage, options = {}) {
    this.root.querySelectorAll('[data-nutrition-stage]').forEach((element) => {
      element.hidden = !element.dataset.nutritionStage.split(/\s+/).includes(stage);
    });
    this.currentStage = stage;

    if (options.persist !== false) {
      this.saveEditorState();
    }
  }

  initDevPreview() {
    const stages = [
      ['mode', 'Mode'],
      ['input', 'Input'],
      ['supplements', 'Supplements'],
      ['loading', 'Loading'],
      ['results', 'Report']
    ];

    const bar = document.createElement('div');
    bar.className = 'nutrition-checker__dev-bar';
    bar.dataset.nutritionDevBar = '';

    const label = document.createElement('span');
    label.className = 'nutrition-checker__dev-bar-label';
    label.textContent = 'Dev preview';
    bar.append(label);

    stages.forEach(([stage, text]) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'nutrition-checker__dev-bar-button';
      button.textContent = text;
      button.addEventListener('click', () => this.goToDevStage(stage));
      bar.append(button);
    });

    this.root.append(bar);

    const requestedStage = new URLSearchParams(window.location.search).get('nc_stage');
    if (requestedStage) {
      this.goToDevStage(requestedStage);
    }
  }

  goToDevStage(stage) {
    if (stage === 'results') {
      this.loadSampleReport();
      return;
    }

    this.setStage(stage, { persist: false });
    const target = this.root.querySelector(`[data-nutrition-stage~="${stage}"]`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  loadSampleReport() {
    const report = buildSampleReport();
    this.lastReport = report;
    this.renderReport(report);
    this.setStage('results', { persist: false });
    const target = this.root.querySelector('[data-nutrition-stage~="results"]');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  stopLoadingProgress() {
    if (this.loadingProgressFrame) {
      window.cancelAnimationFrame(this.loadingProgressFrame);
      this.loadingProgressFrame = null;
    }
  }

  loadingStepsFor(mode, itemCount) {
    if (mode === 'full') {
      return [
        {
          label: copyValue(this.copy, 'messages.loadingFullStack', 'Combining food and supplement data...'),
          detail: `Preparing ${itemCount} diet and supplement ${itemCount === 1 ? 'entry' : 'entries'} for combined review.`
        },
        {
          label: 'Checking food nutrient data',
          detail: 'Confirming USDA nutrient values for your meal entries.'
        },
        {
          label: copyValue(this.copy, 'messages.loadingSupplements', 'Checking supplement labels...'),
          detail: 'Reviewing DSLD label data and user-provided supplement amounts.'
        },
        {
          label: 'Calculating gaps and overages',
          detail: 'Combining food and supplement totals to flag what is low, covered, or running high.'
        },
        {
          label: 'Building your CORE7 comparison',
          detail: 'Mapping nutrient gaps, functional compounds, ingredient audit details, and cost comparison.'
        },
        {
          label: 'Finalizing your report',
          detail: 'Formatting your score, key insights, and support cards.'
        }
      ];
    }

    return [
      {
        label: copyValue(this.copy, 'messages.loadingInitial', 'Analyzing your meals...'),
        detail: `Preparing ${itemCount} food ${itemCount === 1 ? 'entry' : 'entries'} for nutrient review.`
      },
      {
        label: 'Checking food nutrient data',
        detail: 'Confirming USDA nutrient values for your meal entries.'
      },
      {
        label: copyValue(this.copy, 'messages.loadingCrunch', 'Crunching the numbers...'),
        detail: 'Comparing vitamins, minerals, macros, and fiber against daily targets.'
      },
      {
        label: 'Prioritizing your nutrient gaps',
        detail: 'Identifying the gaps most likely to affect your overall score.'
      },
      {
        label: 'Building your CORE7 report',
        detail: 'Formatting your score, insights, and support cards.'
      }
    ];
  }

  startLoadingSequence(mode, itemCount) {
    this.stopLoadingSequence();
    const steps = this.loadingStepsFor(mode, itemCount);
    const list = this.root.querySelector('[data-loading-steps]');
    this.loadingStepIndex = 0;

    if (list) {
      list.innerHTML = '';
      steps.forEach((step, index) => {
        const item = document.createElement('li');
        item.textContent = step.label;
        item.dataset.loadingStep = String(index);
        list.append(item);
      });
    }

    this.updateLoadingStep(steps, 0);
    const startedAt = window.Date.now();

    this.loadingStatusTimer = window.setInterval(() => {
      const elapsed = window.Date.now() - startedAt;
      const nextIndex = Math.min(this.loadingStepIndex + 1, steps.length - 1);
      this.updateLoadingStep(steps, nextIndex);

      if (elapsed > 16000 && nextIndex === steps.length - 1) {
        this.setLoadingDetail('Still working. Larger food or supplement lists can take a little longer while we keep the calculations precise.');
      }
    }, 3600);
  }

  stopLoadingSequence() {
    if (this.loadingStatusTimer) {
      window.clearInterval(this.loadingStatusTimer);
      this.loadingStatusTimer = null;
    }
  }

  updateLoadingStep(steps, index) {
    this.loadingStepIndex = index;
    const step = steps[index];
    const message = this.root.querySelector('[data-loading-message]');
    if (message) message.textContent = step.label;
    this.setLoadingDetail(step.detail);

    this.root.querySelectorAll('[data-loading-step]').forEach((item) => {
      const itemIndex = Number.parseInt(item.dataset.loadingStep, 10);
      item.dataset.loadingState = itemIndex < index ? 'complete' : itemIndex === index ? 'active' : 'pending';
    });
  }

  setLoadingDetail(text) {
    const detail = this.root.querySelector('[data-loading-detail]');
    if (detail) detail.textContent = text;
  }

  setLoadingProgress(percent) {
    const bar = this.root.querySelector('[data-loading-bar]');
    if (!bar) return;
    bar.style.width = `${Math.max(0, Math.min(percent, 100))}%`;
  }

  currentLoadingProgress() {
    const bar = this.root.querySelector('[data-loading-bar]');
    if (!bar) return 0;
    const current = Number.parseFloat(bar.style.width);
    return Number.isFinite(current) ? current : 0;
  }

  driftLoadingProgress(targetPercent, durationMs) {
    this.stopLoadingProgress();
    const startPercent = this.currentLoadingProgress();
    const startTime = window.performance.now();

    const tick = (now) => {
      const elapsed = Math.max(0, now - startTime);
      const eased = 1 - Math.exp(-elapsed / durationMs);
      const nextPercent = startPercent + (targetPercent - startPercent) * eased;
      this.setLoadingProgress(nextPercent);

      if (nextPercent < targetPercent - 0.25) {
        this.loadingProgressFrame = window.requestAnimationFrame(tick);
      } else {
        this.loadingProgressFrame = null;
      }
    };

    this.loadingProgressFrame = window.requestAnimationFrame(tick);
  }

  async prepareDietEntries() {
    const meals = this.getMeals();
    const foods = this.flattenMeals(meals);

    if (foods.length === 0) {
      window.alert(copyValue(this.copy, 'messages.emptyMeals', 'Please enter at least one food item in any meal field.'));
      return null;
    }

    const unmatchedCards = await this.validateAllFoodCards();
    if (unmatchedCards.length > 0) {
      this.query(`[data-food-cards="${unmatchedCards[0].meal}"]`).scrollIntoView({ behavior: 'smooth', block: 'center' });
      return null;
    }

    return { meals, foods };
  }

  async handleDietSubmit() {
    const prepared = await this.prepareDietEntries();
    if (!prepared) return;

    if (this.analysisMode === 'full') {
      this.pendingFullStackMeals = prepared.meals;
      this.pendingFullStackFoods = prepared.foods;
      this.setStage('supplements');
      this.query('[data-nutrition-stage="supplements"]').scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    await this.analyzeQuick(prepared);
  }

  async analyzeQuick(prepared) {
    const meals = prepared.meals;
    const foods = prepared.foods;

    this.setStage('loading');
    this.startLoadingSequence('quick', foods.length);
    this.setLoadingProgress(12);
    this.query('[data-nutrition-stage="loading"]').scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.clearFoodValidationMessages();
    this.root.querySelectorAll('[data-unresolved-notice]').forEach((element) => element.remove());

    try {
      this.driftLoadingProgress(38, 1200);

      const payload = {
        foods: foods.map((item) => ({
          meal: item.meal,
          name: item.name,
          serving_phrase: item.servingPhrase,
          serving_grams: item.servingGrams,
          confidence: item.confidence,
          food_confidence: item.food_confidence,
          serving_confidence: item.serving_confidence
        }))
      };

      this.setLoadingProgress(Math.max(this.currentLoadingProgress(), 42));
      this.driftLoadingProgress(94, Math.min(Math.max(foods.length * 900, 5000), 14000));
      const data = await this.apiPost('/analyze', payload);
      this.stopLoadingProgress();
      this.stopLoadingSequence();

      if (data.status !== 'ok') {
        // All-fail case: nothing could be matched. Show the backend message plus
        // the unresolved list as a non-blocking notice instead of a generic error.
        if (Array.isArray(data.unresolved_foods)) {
          this.setStage('input');
          this.renderUnresolvedFoods(data.unresolved_foods, 'input', {
            message: data.message || copyValue(this.copy, 'messages.apiUnavailable', 'Nutrition service is unavailable.')
          });
          this.query('[data-nutrition-stage="input"]').scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        throw new Error(data.message || copyValue(this.copy, 'messages.apiUnavailable', 'Nutrition service is unavailable.'));
      }

      this.setLoadingProgress(100);
      await sleep(200);

      this.lastReport = transformAnalyzeResponse(data, meals);
      this.renderReport(this.lastReport);
      this.setStage('results');
      // Non-blocking notice for foods that couldn't be matched; the report still
      // reflects the foods that resolved. An empty list clears any prior notice.
      this.renderUnresolvedFoods(data.unresolved_foods || [], 'results', {
        note: copyValue(this.copy, 'messages.unresolvedNote', 'The rest of your report still reflects the foods we analyzed.')
      });
      this.query('[data-nutrition-stage="results"]').scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      this.stopLoadingProgress();
      this.stopLoadingSequence();
      window.alert(error.message);
      this.setStage('input');
    }
  }

  async analyzeFullStack(skipSupplements) {
    if (!this.pendingFullStackMeals || this.pendingFullStackFoods.length === 0) {
      const prepared = await this.prepareDietEntries();
      if (!prepared) return;
      this.pendingFullStackMeals = prepared.meals;
      this.pendingFullStackFoods = prepared.foods;
    }

    const monthlySpend = skipSupplements ? 0 : parseMoneyValue(this.query('[data-supplement-spend]').value);
    let supplements = [];

    if (!skipSupplements) {
      const cards = Array.from(this.query('[data-supplement-cards]').querySelectorAll('[data-supplement-card]'));
      supplements = this.getSupplements();
      if (cards.length === 0 || supplements.length === 0) {
        window.alert(copyValue(this.copy, 'messages.emptySupplements', 'Add at least one supplement or use the skip option.'));
        return;
      }
    }

    if (!skipSupplements && supplements.length === 0) {
      window.alert(copyValue(this.copy, 'messages.emptySupplements', 'Add at least one supplement or use the skip option.'));
      return;
    }

    if (!skipSupplements) {
      const unmatchedCards = await this.validateAllSupplementCards();
      if (unmatchedCards.length > 0) {
        const targetCard = unmatchedCards[0];
        if (targetCard) {
          targetCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      supplements = this.getSupplements();
    }

    this.setStage('loading');
    this.startLoadingSequence('full', this.pendingFullStackFoods.length + supplements.length);
    this.setLoadingProgress(12);
    this.query('[data-nutrition-stage="loading"]').scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.root.querySelectorAll('[data-unresolved-notice]').forEach((element) => element.remove());

    try {
      this.driftLoadingProgress(36, 1200);

      const payload = {
        foods: this.pendingFullStackFoods.map((item) => ({
          meal: item.meal,
          name: item.name,
          serving_phrase: item.servingPhrase,
          serving_grams: item.servingGrams,
          confidence: item.confidence,
          food_confidence: item.food_confidence,
          serving_confidence: item.serving_confidence,
          ...(Number.isInteger(item.fdcId) ? { fdc_id: item.fdcId } : {})
        })),
        supplements,
        monthly_supplement_spend: monthlySpend,
        core7_monthly_price: this.core7MonthlyPrice
      };

      this.setLoadingProgress(Math.max(this.currentLoadingProgress(), 44));
      this.driftLoadingProgress(94, Math.min(Math.max((this.pendingFullStackFoods.length + supplements.length) * 850, 5200), 16000));
      const data = await this.apiPost('/analyze-full-stack', payload);
      this.stopLoadingProgress();
      this.stopLoadingSequence();

      if (data.status !== 'ok') {
        // All-fail case: nothing could be matched. Show the backend message plus
        // the unresolved list as a non-blocking notice instead of a generic error.
        if (Array.isArray(data.unresolved_foods)) {
          this.setStage('supplements');
          this.renderUnresolvedFoods(data.unresolved_foods, 'supplements', {
            message: data.message || copyValue(this.copy, 'messages.apiUnavailable', 'Nutrition service is unavailable.')
          });
          this.query('[data-nutrition-stage="supplements"]').scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        throw new Error(data.message || copyValue(this.copy, 'messages.apiUnavailable', 'Nutrition service is unavailable.'));
      }

      this.setLoadingProgress(100);
      await sleep(200);

      this.lastReport = transformFullStackResponse(data, this.pendingFullStackMeals);
      this.renderReport(this.lastReport);
      this.setStage('results');
      // Non-blocking notice for foods that couldn't be matched; the report still
      // reflects the foods that resolved. An empty list clears any prior notice.
      this.renderUnresolvedFoods(data.unresolved_foods || [], 'results', {
        note: copyValue(this.copy, 'messages.unresolvedNote', 'The rest of your report still reflects the foods we analyzed.')
      });
      this.query('[data-nutrition-stage="results"]').scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      this.stopLoadingProgress();
      this.stopLoadingSequence();
      window.alert(error.message);
      this.setStage('supplements');
    }
  }

  async searchFood(query) {
    const data = await this.apiGet('/foods/search', { query });
    if (data.status !== 'ok' || !Array.isArray(data.foods)) {
      throw new Error(copyValue(this.copy, 'messages.usdaNoFoods', 'USDA response did not include foods.'));
    }
    return data.foods.map((food) => ({
      fdcId: food.fdcId,
      description: food.description,
      dataType: food.dataType
    }));
  }

  selectFoodMatch(query, foods) {
    if (foods.length === 0) return { food: null, suggestions: [] };
    const priority = { Foundation: 0, 'SR Legacy': 1, 'Survey (FNDDS)': 2, Branded: 3 };
    const sorted = [...foods].sort((a, b) => {
      const first = priority[a.dataType];
      const second = priority[b.dataType];
      const firstScore = Number.isInteger(first) ? first : 4;
      const secondScore = Number.isInteger(second) ? second : 4;
      return firstScore - secondScore;
    });
    const food = sorted.find((item) => isLikelyFoodMatch(query, item.description));
    return {
      food: food || null,
      suggestions: uniqueFoodSuggestions(foods)
    };
  }

  renderReport(report) {
    this.renderScore(report.score, report.scoreBand);
    this.renderFoods(report.foods);
    this.renderMacros(report.macroTotals);
    this.renderSupplementSections(report);
    this.renderNutrients(report.totals);
    this.renderInsights(report);
    this.renderBenefits(report.totals, report.benefitGaps);
    this.renderProjectedScore(report);
    this.renderErrors(report.errors);
  }

  renderScore(score, scoreBand) {
    const value = this.query('[data-score-value]');
    const message = this.query('[data-score-message]');
    const badge = this.query('[data-score-badge]');
    const ring = this.query('[data-score-ring]');
    const circumference = 2 * Math.PI * 50;
    const level = scoreLevel(score);
    const band = scoreBand || scoreBandFromCopy(score, this.copy);

    value.textContent = String(score);
    value.style.color = level.color;
    badge.textContent = band.label;
    badge.style.color = level.color;
    ring.style.stroke = level.color;
    window.requestAnimationFrame(() => {
      ring.style.strokeDashoffset = String(circumference * (1 - score / 100));
    });

    message.textContent = band.message;
  }

  renderInsights(report) {
    const container = this.root.querySelector('[data-insights]');
    if (!container) return;
    container.innerHTML = '';

    if (!report.insights) return;

    if (report.insights.summary) {
      const summary = document.createElement('p');
      summary.textContent = report.insights.summary;
      container.append(summary);
    }

    if (Array.isArray(report.foodFirstSuggestions) && report.foodFirstSuggestions.length > 0) {
      const heading = document.createElement('h4');
      heading.textContent = copyValue(this.copy, 'messages.foodFirstHeading', 'Food-first ideas');
      const list = document.createElement('ul');
      report.foodFirstSuggestions.forEach((item) => {
        const li = document.createElement('li');
        li.textContent = item;
        list.append(li);
      });
      container.append(heading, list);
    }

    if (Array.isArray(report.core7Fit) && report.core7Fit.length > 0) {
      const heading = document.createElement('h4');
      heading.textContent = copyValue(this.copy, 'messages.core7FitHeading', 'How CORE7 may help');
      const list = document.createElement('ul');
      report.core7Fit.forEach((item) => {
        const li = document.createElement('li');
        li.textContent = item;
        list.append(li);
      });
      container.append(heading, list);
    }
  }

  renderFoods(items) {
    const container = this.query('[data-foods-analyzed]');
    container.innerHTML = '';

    items.forEach((item) => {
      const tag = document.createElement('div');
      tag.className = 'nutrition-checker__food-tag';

      const name = document.createElement('strong');
      name.textContent = item.food ? `${item.query}` : `${item.query} ${copyValue(this.copy, 'messages.notFoundSuffix', 'not found')}`;

      const match = document.createElement('small');
      if (item.food) {
        match.textContent = `${item.food.description} (${item.food.dataType})`;
      } else {
        match.textContent = item.error || copyValue(this.copy, 'messages.notFoundDetail', 'Could not find this food in USDA FoodData Central.');
      }

      const amount = document.createElement('small');
      amount.textContent = copyTemplate(
        this.copy,
        'messages.estimateUsed',
        { serving: item.servingPhrase, grams: item.servingGrams },
        `Estimated serving used: ${item.servingPhrase}, about ${item.servingGrams}g.`
      );

      tag.append(name, match, amount);
      if (item.portionNotes) {
        const notes = document.createElement('small');
        notes.textContent = item.portionNotes;
        tag.append(notes);
      }
      container.append(tag);
    });
  }

  renderFoodValidationMessages(items) {
    items.forEach((item) => {
      const card = Array.from(this.query(`[data-food-cards="${item.meal}"]`).querySelectorAll('[data-food-card]')).find((element) => {
        return element.querySelector('[data-food-name]').value.trim() === item.query;
      });
      if (!card) return;
      this.renderFoodValidationMessage(card, item);
    });
  }

  renderFoodValidationMessage(card, item) {
    const message = card.querySelector('[data-card-message]');
    message.innerHTML = '';

    const text = document.createElement('p');
    text.textContent = item.error || copyTemplate(
      this.copy,
      'messages.foodNotFound',
      { food: item.query },
      `We could not find "${item.query}" as a food. Try a more specific food name before analyzing.`
    );
    message.append(text);

    if (!Array.isArray(item.suggestions) || item.suggestions.length === 0) return;

    const label = document.createElement('span');
    label.textContent = copyValue(this.copy, 'messages.suggestionsLabel', 'Did you mean:');
    const suggestions = document.createElement('div');
    suggestions.className = 'nutrition-checker__suggestions';
    item.suggestions.forEach((suggestion) => {
      const suggestionLabel = suggestion.description;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'nutrition-checker__suggestion-button';
      button.textContent = suggestionLabel;
      button.setAttribute(
        'aria-label',
        copyTemplate(this.copy, 'messages.useSuggestionLabel', { food: suggestionLabel }, `Use ${suggestionLabel}`)
      );
      button.addEventListener('click', () => {
        const fdcId = parseFdcId(suggestion.fdcId);
        card.querySelector('[data-food-name]').value = suggestionLabel;
        card.dataset.foodStatus = 'matched';
        card.dataset.fdcId = Number.isInteger(fdcId) ? String(fdcId) : '';
        message.innerHTML = '';
      });
      suggestions.append(button);
    });
    message.append(label, suggestions);
  }

  renderMacros(totals) {
    const container = this.query('[data-macros-grid]');
    container.innerHTML = '';

    Object.entries(this.macroDefinitions).forEach(([key, definition]) => {
      const amount = totals[key];
      const pct = (amount / definition.target) * 100;
      const card = document.createElement('div');
      card.className = 'nutrition-checker__macro-card';

      const copy = document.createElement('div');
      const label = document.createElement('strong');
      label.textContent = definition.name;
      const detail = document.createElement('small');
      detail.textContent = `${formatAmount(amount)} ${definition.unit} / ${definition.target} ${definition.unit}`;
      copy.append(label, detail);

      const percent = document.createElement('strong');
      percent.textContent = `${Math.round(Math.min(pct, 999))}%`;
      percent.style.color = scoreLevel(pct).color;

      card.append(copy, percent);
      container.append(card);
    });
  }

  renderSupplementSections(report) {
    const isFullStack = report.analysisMode === 'full';
    const supplementResults = this.query('[data-supplement-results]');
    const supplementSection = this.query('[data-supplement-report-section]');
    const functionalCompoundsSection = this.query('[data-functional-compounds-section]');
    const contributionSection = this.query('[data-supplement-contribution-section]');
    const overageSection = this.query('[data-overage-section]');
    const costComparisonSection = this.query('[data-cost-comparison-section]');
    const auditSection = this.query('[data-ingredient-audit-section]');

    supplementResults.hidden = !isFullStack;
    [supplementSection, functionalCompoundsSection, contributionSection, overageSection, costComparisonSection, auditSection].forEach((section) => {
      section.hidden = !isFullStack;
    });

    if (!isFullStack) {
      return;
    }

    this.renderCombinedScore(report);
    this.renderSupplementsAnalyzed(report.supplements);
    this.renderFunctionalCompounds(report.functionalCompounds);
    this.renderSupplementContributions(report.supplementTotals);
    this.renderOverages(report.overages);
    this.renderCostComparison(report);
    this.renderIngredientAudit(report.syntheticIngredientAudit);
  }

  renderCombinedScore(report) {
    const container = this.query('[data-combined-score]');
    container.innerHTML = '';

    const foodTotals = subtractTotals(report.totals, report.supplementTotals, nutrients);
    const foodScore = calculateScore(foodTotals);
    const combinedScore = report.score;
    const supplementBoost = Math.max(0, combinedScore - foodScore);
    const foodColor = '#40916C';
    const supplementColor = '#4C72C4';
    const combinedColor = '#B7790F';

    const wrapper = document.createElement('div');
    wrapper.className = 'nutrition-checker__combined-score';

    const row = document.createElement('div');
    row.className = 'nutrition-checker__combined-score-row';

    [
      ['Food Score', foodScore, foodColor],
      ['Supplement Boost', supplementBoost, supplementColor],
      ['Combined Score', combinedScore, combinedColor]
    ].forEach(([label, value, color], index) => {
      if (index > 0) {
        const plus = document.createElement('div');
        plus.className = 'nutrition-checker__combined-score-plus';
        plus.textContent = index === 2 ? '=' : '+';
        row.append(plus);
      }

      const item = document.createElement('div');
      item.className = 'nutrition-checker__combined-score-item';
      const score = document.createElement('strong');
      score.textContent = String(value);
      score.style.color = color;
      const labelElement = document.createElement('span');
      labelElement.textContent = label;
      item.append(score, labelElement);
      row.append(item);
    });

    const bar = document.createElement('div');
    bar.className = 'nutrition-checker__combined-bar';
    const foodFill = document.createElement('span');
    foodFill.style.width = `${Math.min(foodScore, 100)}%`;
    foodFill.style.background = foodColor;
    const supplementFill = document.createElement('span');
    supplementFill.style.width = `${Math.min(supplementBoost, Math.max(0, 100 - foodScore))}%`;
    supplementFill.style.background = supplementColor;
    bar.append(foodFill, supplementFill);

    const legend = document.createElement('div');
    legend.className = 'nutrition-checker__combined-legend';
    [
      ['Food', foodColor],
      ['Supplements', supplementColor]
    ].forEach(([label, color]) => {
      const item = document.createElement('span');
      const dot = document.createElement('span');
      dot.className = 'nutrition-checker__combined-dot';
      dot.style.background = color;
      item.append(dot, document.createTextNode(label));
      legend.append(item);
    });

    wrapper.append(row, bar, legend);
    container.append(wrapper);
  }

  renderSupplementsAnalyzed(items) {
    const container = this.query('[data-supplements-analyzed]');
    container.innerHTML = '';

    if (!Array.isArray(items) || items.length === 0) {
      container.append(this.createReportMessage(copyValue(this.copy, 'messages.noSupplementsReported', 'No daily supplements were included.')));
      return;
    }

    items.forEach((item) => {
      const card = document.createElement('div');
      card.className = 'nutrition-checker__supplement-card';
      const title = document.createElement('strong');
      title.textContent = item.data_source === 'user_provided' ? item.name : item.full_name;
      const brand = document.createElement('small');
      brand.textContent = item.data_source === 'user_provided' ? item.brand : item.brand_name;
      const serving = document.createElement('small');
      serving.textContent = item.serving_summary;
      const source = document.createElement('small');
      source.textContent = item.data_source === 'user_provided'
        ? copyValue(this.copy, 'messages.userProvidedEstimate', 'User-provided estimate')
        : `DSLD label ${item.label_id}`;
      card.append(title, brand, serving, source);
      if (item.estimated_nutrients) {
        const estimated = document.createElement('small');
        estimated.textContent = copyValue(
          this.copy,
          'messages.estimatedNutrientsNote',
          'Nutrient amount estimated from the provided amount and unit.'
        );
        card.append(estimated);
      }
      container.append(card);
    });
  }

  renderSupplementContributions(totals) {
    const container = this.query('[data-supplement-contributions]');
    container.innerHTML = '';
    const entries = Object.entries(nutrients).filter(([key]) => {
      const amount = totals[key];
      return Number.isFinite(amount) && amount > 0;
    });

    if (entries.length === 0) {
      container.append(this.createReportMessage(copyValue(this.copy, 'messages.noSupplementContributions', 'No measurable supplement nutrient contributions were found.')));
      return;
    }

    entries.forEach(([key, definition]) => {
      const amount = totals[key];
      const pct = (amount / definition.rda) * 100;
      const card = document.createElement('div');
      card.className = 'nutrition-checker__macro-card';
      const copy = document.createElement('div');
      const label = document.createElement('strong');
      label.textContent = definition.name;
      const detail = document.createElement('small');
      detail.textContent = `${formatAmount(amount)} ${definition.unit} from supplements`;
      copy.append(label, detail);
      const percent = document.createElement('strong');
      percent.textContent = `${Math.round(Math.min(pct, 999))}%`;
      percent.style.color = scoreLevel(pct).color;
      card.append(copy, percent);
      container.append(card);
    });
  }

  renderFunctionalCompounds(items) {
    const container = this.query('[data-functional-compounds]');
    container.innerHTML = '';

    if (!Array.isArray(items) || items.length === 0) {
      container.append(this.createReportMessage(copyValue(this.copy, 'messages.noFunctionalCompounds', 'No functional compounds were available from matched supplement labels.')));
      return;
    }

    items.forEach((item) => {
      const card = document.createElement('div');
      card.className = 'nutrition-checker__food-tag';
      const name = document.createElement('strong');
      name.textContent = item.name;
      const category = document.createElement('small');
      category.textContent = item.category;
      const source = document.createElement('small');
      source.textContent = item.data_source === 'user_provided'
        ? copyValue(this.copy, 'messages.userProvidedFunctionalCompound', 'Estimated non-DV compound from user-provided entry')
        : item.source;
      const amount = document.createElement('small');
      amount.textContent = item.amount_summary;
      card.append(name, category, source, amount);
      if (item.estimated_nutrients) {
        const estimated = document.createElement('small');
        estimated.textContent = copyValue(
          this.copy,
          'messages.estimatedNutrientsNote',
          'Nutrient amount estimated from the provided amount and unit.'
        );
        card.append(estimated);
      }
      container.append(card);
    });
  }

  renderOverages(items) {
    const container = this.query('[data-overage-list]');
    container.innerHTML = '';

    if (!Array.isArray(items) || items.length === 0) {
      container.append(this.createReportMessage(copyValue(this.copy, 'messages.noOverages', 'No likely nutrient overages were flagged.')));
      return;
    }

    items.forEach((item) => {
      const card = document.createElement('div');
      card.className = 'nutrition-checker__food-tag';
      const name = document.createElement('strong');
      name.textContent = item.name;
      const amount = document.createElement('small');
      amount.textContent = `${formatAmount(item.amount)} ${item.unit} / ${formatAmount(item.limit)} ${item.unit}`;
      const source = document.createElement('small');
      source.textContent = item.source;
      card.append(name, amount, source);
      container.append(card);
    });
  }

  renderCostComparison(report) {
    const container = this.query('[data-cost-comparison]');
    container.innerHTML = '';
    const costComparison = report.costComparison;

    if (!costComparison || !Number.isFinite(costComparison.monthly_supplement_spend) || !Number.isFinite(this.core7MonthlyPrice)) {
      container.append(this.createReportMessage(copyValue(this.copy, 'messages.noCostComparison', 'Add monthly supplement spend and configure the CORE7 monthly price to compare costs.')));
      return;
    }

    const currentSpend = costComparison.monthly_supplement_spend;
    const core7Price = this.core7MonthlyPrice;
    const monthlyDifference = core7Price - currentSpend;
    const supplementNames = supplementDisplayNames(report.supplements);
    const supplementSummary = supplementNames.length > 0 ? supplementNames.join(' · ') : 'Your supplements';
    const comparison = document.createElement('div');
    comparison.className = 'nutrition-checker__cost-comparison';

    const currentCard = this.createCostCard(
      copyValue(this.copy, 'messages.monthlySpendLabel', 'Current stack'),
      currentSpend,
      [
        supplementSummary,
        'Mostly synthetic isolates',
        'Missing: phytonutrients, adaptogens, probiotics'
      ]
    );
    const vs = document.createElement('div');
    vs.className = 'nutrition-checker__cost-vs';
    vs.textContent = 'VS';
    const core7Card = this.createCostCard(
      copyValue(this.copy, 'messages.core7PriceLabel', 'CORE7'),
      core7Price,
      [
        '51+ whole-food ingredients',
        'Covers: all major nutrient gaps',
        'Plus: adaptogens, probiotics, phytonutrients no pill contains'
      ],
      'nutrition-checker__cost-card--core7'
    );
    comparison.append(currentCard, vs, core7Card);
    container.append(comparison);

    const summary = document.createElement('p');
    summary.className = 'nutrition-checker__cost-summary';
    summary.textContent = costDifferenceSummary(monthlyDifference);
    container.append(summary);

    const ctaUrl = copyValue(this.copy, 'messages.core7CtaUrl', '');
    if (ctaUrl) {
      const ctaRow = document.createElement('div');
      ctaRow.className = 'nutrition-checker__cost-cta-row';
      const cta = document.createElement('a');
      cta.className = 'nutrition-checker__cost-cta';
      cta.href = ctaUrl;
      cta.textContent = copyValue(this.copy, 'messages.core7CtaLabel', 'Try CORE7');
      if (this.copy?.messages?.core7CtaNewWindow) {
        cta.target = '_blank';
        cta.rel = 'noopener noreferrer';
      }
      ctaRow.append(cta);
      container.append(ctaRow);
    }
  }

  createCostCard(labelText, value, notes, modifierClass = '') {
    const card = document.createElement('div');
    card.className = `nutrition-checker__cost-card ${modifierClass}`.trim();
    const label = document.createElement('div');
    label.className = 'nutrition-checker__cost-label';
    label.textContent = labelText;
    const amount = document.createElement('div');
    amount.className = 'nutrition-checker__cost-amount';
    amount.append(document.createTextNode(formatCurrencyCompact(value)));
    const cadence = document.createElement('span');
    cadence.textContent = '/mo';
    amount.append(cadence);
    card.append(label, amount);
    notes.forEach((noteText) => {
      const note = document.createElement('small');
      note.textContent = noteText;
      card.append(note);
    });
    return card;
  }

  renderIngredientAudit(items) {
    const container = this.query('[data-ingredient-audit]');
    container.innerHTML = '';

    if (!Array.isArray(items) || items.length === 0) {
      container.append(this.createReportMessage(copyValue(this.copy, 'messages.noIngredientAudit', 'No extra supplement ingredients were available for audit.')));
      return;
    }

    items.forEach((item) => {
      const details = document.createElement('details');
      details.className = 'nutrition-checker__beyond-card';
      details.style.setProperty('--beyond-color', '#2e4e5d');
      const summary = document.createElement('summary');
      const icon = document.createElement('span');
      icon.className = 'nutrition-checker__beyond-icon';
      icon.textContent = benefitInitial(item.supplement);
      const copy = document.createElement('span');
      copy.className = 'nutrition-checker__beyond-copy';
      const title = document.createElement('strong');
      title.textContent = item.supplement;
      const count = document.createElement('small');
      count.textContent = `${item.ingredients.length} ingredients`;
      copy.append(title, count);
      const arrow = document.createElement('span');
      arrow.className = 'nutrition-checker__beyond-arrow';
      arrow.textContent = '+';
      summary.append(icon, copy, arrow);
      const list = document.createElement('ul');
      item.ingredients.forEach((ingredient) => {
        const li = document.createElement('li');
        const strong = document.createElement('strong');
        strong.textContent = `${ingredient.name}: `;
        li.append(strong, document.createTextNode(ingredient.category));
        list.append(li);
      });
      details.append(summary, list);
      container.append(details);
    });
  }

  createReportMessage(text) {
    const message = document.createElement('div');
    message.className = 'nutrition-checker__food-tag';
    const copy = document.createElement('small');
    copy.textContent = text;
    message.append(copy);
    return message;
  }

  renderNutrients(totals) {
    const container = this.query('[data-nutrient-list]');
    container.innerHTML = '';
    const groups = groupDefinitions(nutrients);

    Object.entries(groups).forEach(([group, entries]) => {
      const groupElement = document.createElement('div');
      groupElement.className = 'nutrition-checker__nutrient-group';
      const heading = document.createElement('h4');
      heading.textContent = copyValue(this.copy, ['groups', group], group);
      const grid = document.createElement('div');
      grid.className = 'nutrition-checker__nutrient-grid';

      entries.forEach(([key, definition]) => {
        const amount = totals[key];
        const pct = (amount / definition.rda) * 100;
        const level = scoreLevel(pct);
        const card = document.createElement('div');
        card.className = 'nutrition-checker__nutrient-card';
        card.dataset.nutrientStatus = level.label;
        card.style.color = level.color;
        card.style.setProperty('--nutrient-color', level.color);

        const header = document.createElement('div');
        header.className = 'nutrition-checker__nutrient-header';
        const label = document.createElement('strong');
        label.textContent = definition.name;
        const status = document.createElement('span');
        status.className = 'nutrition-checker__nutrient-status';
        status.textContent = pct >= 75 ? '+' : 'x';
        status.setAttribute('aria-label', copyValue(this.copy, ['status', level.label], level.label));
        header.append(label, status);

        const values = document.createElement('div');
        values.className = 'nutrition-checker__nutrient-values';
        const amountText = document.createElement('span');
        amountText.innerHTML = `<strong>${formatAmount(amount)} ${definition.unit}</strong> / ${definition.rda} ${definition.unit}`;
        const percentText = document.createElement('strong');
        percentText.textContent = `${Math.round(Math.min(pct, 999))}%`;
        values.append(amountText, percentText);

        const bar = document.createElement('div');
        bar.className = 'nutrition-checker__bar';
        const fill = document.createElement('span');
        fill.style.width = `${Math.min(pct, 100)}%`;
        bar.append(fill);

        card.append(header, values, bar);
        grid.append(card);
      });

      groupElement.append(heading, grid);
      container.append(groupElement);
    });
  }

  renderBenefits(totals, benefitGaps) {
    const container = this.query('[data-benefits-grid]');
    container.innerHTML = '';

    const ranked = this.coreBenefits.map((benefit) => {
      const apiGap = Array.isArray(benefitGaps)
        ? benefitGaps.find((item) => item.key === benefit.key)
        : null;

      const nutrientData = benefit.nutrients.map((key) => {
        const definition = nutrients[key];
        const pct = Math.min((totals[key] / definition.rda) * 100, 100);
        return { key, definition, pct };
      });
      const avgPct = nutrientData.reduce((sum, item) => sum + item.pct, 0) / nutrientData.length;
      const gap = apiGap ? apiGap.gap_percent : Math.max(0, Math.round(100 - avgPct));
      const deficient = apiGap
        ? apiGap.deficient_nutrients.map((item) => ({
            definition: { name: item.name },
            pct: item.percent_dv
          }))
        : nutrientData.filter((item) => item.pct < 75).sort((a, b) => a.pct - b.pct);
      return { ...benefit, gap, deficient };
    }).sort((a, b) => b.gap - a.gap);

    ranked.forEach((benefit) => {
      const core7Additions = this.beyondNutrition.find((category) => category.key === benefit.key);
      const card = document.createElement('article');
      card.className = 'nutrition-checker__benefit-card';
      card.style.setProperty('--benefit-color', benefit.color);

      const header = document.createElement('div');
      header.className = 'nutrition-checker__benefit-header';
      const icon = document.createElement('span');
      icon.className = 'nutrition-checker__benefit-icon';
      icon.textContent = benefitInitial(benefit.name);
      const copy = document.createElement('div');
      const title = document.createElement('h4');
      title.textContent = benefit.name;
      title.style.color = benefit.color;
      const desc = document.createElement('p');
      desc.textContent = benefit.description;
      copy.append(title, desc);
      const score = document.createElement('div');
      score.className = 'nutrition-checker__benefit-score';
      const scoreValue = document.createElement('strong');
      scoreValue.textContent = `${benefit.gap}%`;
      const scoreLabel = document.createElement('span');
      scoreLabel.textContent = copyValue(this.copy, 'messages.gapSuffix', 'gap').toUpperCase();
      score.append(scoreValue, scoreLabel);
      score.setAttribute('aria-label', `${benefit.gap}% ${copyValue(this.copy, 'messages.gapSuffix', 'gap')}`);
      header.append(icon, copy, score);

      const gaps = document.createElement('div');
      gaps.className = 'nutrition-checker__gap-tags';
      if (benefit.deficient.length === 0) {
        const tag = document.createElement('span');
        tag.textContent = copyValue(this.copy, 'messages.onTrack', 'On track for this benefit area');
        gaps.append(tag);
      } else {
        benefit.deficient.forEach((item) => {
          const tag = document.createElement('span');
          tag.textContent = `${item.definition.name}: ${Math.round(item.pct)}%`;
          gaps.append(tag);
        });
      }

      const detail = document.createElement('small');
      detail.className = 'nutrition-checker__benefit-detail';
      detail.textContent = benefit.detail;
      card.append(header, gaps, detail);

      if (core7Additions && core7Additions.ingredients.length > 0) {
        const additions = document.createElement('div');
        additions.className = 'nutrition-checker__benefit-ingredients';
        const additionsHeading = document.createElement('h5');
        additionsHeading.textContent = copyValue(this.copy, 'messages.core7IngredientsLabel', 'CORE7 adds');
        const list = document.createElement('ul');

        core7Additions.ingredients.forEach(([name, additionBenefit]) => {
          const item = document.createElement('li');
          const ingredient = document.createElement('strong');
          ingredient.textContent = `${name}: `;
          item.append(ingredient, document.createTextNode(additionBenefit));
          list.append(item);
        });

        additions.append(additionsHeading, list);

        const collapseAfter = 3;
        const total = core7Additions.ingredients.length;
        if (total > collapseAfter) {
          additions.classList.add('is-collapsed');
          const toggle = document.createElement('button');
          toggle.type = 'button';
          toggle.className = 'nutrition-checker__benefit-toggle';
          toggle.setAttribute('aria-expanded', 'false');
          const toggleLabel = document.createElement('span');
          toggleLabel.className = 'nutrition-checker__benefit-toggle-label';
          toggleLabel.textContent = `Show all ${total}`;
          const toggleArrow = document.createElement('span');
          toggleArrow.className = 'nutrition-checker__beyond-arrow';
          toggleArrow.textContent = '+';
          toggle.append(toggleLabel, toggleArrow);
          toggle.addEventListener('click', () => {
            const collapsed = additions.classList.toggle('is-collapsed');
            toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
            toggleLabel.textContent = collapsed ? `Show all ${total}` : 'Show less';
          });
          additions.append(toggle);
        }

        card.append(additions);
      }
      container.append(card);
    });
  }

  renderProjectedScore(report) {
    const container = this.root.querySelector('[data-core7-projection]');
    if (!container) return;

    const totals = report.totals || {};
    const core7 = this.core7Nutrients || {};
    const currentScore = Number.isFinite(report.score) ? report.score : null;

    if (Object.keys(core7).length === 0 || currentScore === null) {
      container.hidden = true;
      return;
    }

    const totalsWithCore7 = { ...totals };
    Object.keys(core7).forEach((key) => {
      const base = Number.isFinite(totals[key]) ? totals[key] : 0;
      totalsWithCore7[key] = base + core7[key].amount;
    });

    const baseLocal = calculateScore(totals);
    const projectedLocal = calculateScore(totalsWithCore7);
    const delta = Math.max(0, projectedLocal - baseLocal);
    const projectedScore = Math.min(100, currentScore + delta);
    const addWidth = Math.max(0, projectedScore - currentScore);

    const gaps = Object.keys(core7)
      .map((key) => {
        const definition = nutrients[key];
        if (!definition) return null;
        const current = Number.isFinite(totals[key]) ? totals[key] : 0;
        const beforePct = Math.min((current / definition.rda) * 100, 100);
        const afterPct = Math.min(((current + core7[key].amount) / definition.rda) * 100, 100);
        return {
          name: core7[key].displayName || definition.name,
          beforePct,
          afterPct,
          improvement: afterPct - beforePct
        };
      })
      .filter((item) => item && item.beforePct < 100 && item.improvement > 0)
      .sort((a, b) => b.improvement - a.improvement)
      .slice(0, 4);

    if (gaps.length === 0) {
      container.hidden = true;
      return;
    }

    container.hidden = false;

    const nowEl = container.querySelector('[data-core7-score-now]');
    const nextEl = container.querySelector('[data-core7-score-next]');
    const deltaEl = container.querySelector('[data-core7-score-delta]');
    if (nowEl) nowEl.textContent = String(currentScore);
    if (nextEl) nextEl.textContent = String(projectedScore);
    if (deltaEl) deltaEl.textContent = `+${projectedScore - currentScore}`;

    const barNow = container.querySelector('.nutrition-checker__core7-bar-now');
    const barAdd = container.querySelector('.nutrition-checker__core7-bar-add');
    if (barNow) barNow.style.width = `${currentScore}%`;
    if (barAdd) barAdd.style.width = `${addWidth}%`;

    const list = container.querySelector('[data-core7-gap-list]');
    if (list) {
      list.innerHTML = '';
      gaps.forEach((gap) => {
        const li = document.createElement('li');
        li.className = 'nutrition-checker__core7-gap';

        const name = document.createElement('span');
        name.className = 'nutrition-checker__core7-gap-name';
        name.textContent = gap.name;

        const track = document.createElement('span');
        track.className = 'nutrition-checker__core7-gap-track';
        const now = document.createElement('span');
        now.className = 'nutrition-checker__core7-gap-now';
        now.style.width = `${Math.round(gap.beforePct)}%`;
        const add = document.createElement('span');
        add.className = 'nutrition-checker__core7-gap-add';
        add.style.left = `${Math.round(gap.beforePct)}%`;
        add.style.width = `${Math.round(gap.afterPct - gap.beforePct)}%`;
        track.append(now, add);

        const pct = document.createElement('span');
        pct.className = 'nutrition-checker__core7-gap-pct';
        pct.textContent = `${Math.round(gap.beforePct)}% \u2192 ${Math.round(gap.afterPct)}%`;

        li.append(name, track, pct);
        list.append(li);
      });
    }

    const foot = container.querySelector('.nutrition-checker__core7-projection-foot');
    if (foot) {
      foot.textContent = copyValue(
        this.copy,
        'messages.core7ProjectionFoot',
        'Projected using CORE7\u2019s per-serving nutrient amounts.'
      );
    }
  }

  renderErrors(errors) {
    this.root.querySelectorAll('[data-error-banner]').forEach((element) => element.remove());
    if (errors.length === 0) return;

    const banner = document.createElement('div');
    banner.className = 'nutrition-checker__food-tag';
    banner.dataset.errorBanner = '';
    const title = document.createElement('strong');
    title.textContent = copyValue(this.copy, 'messages.errorHeading', 'Some foods could not be analyzed.');
    const detail = document.createElement('small');
    detail.textContent = errors[0];
    banner.append(title, detail);
    this.query('[data-nutrition-stage="results"]').prepend(banner);
  }

  renderUnresolvedFoods(unresolvedFoods, stage, options = {}) {
    // Always clear any prior notice first, so an empty list (everything matched)
    // leaves no stale notice behind.
    this.root.querySelectorAll('[data-unresolved-notice]').forEach((element) => element.remove());
    const list = Array.isArray(unresolvedFoods) ? unresolvedFoods.filter(Boolean) : [];
    // Only render when there is something to report: either unresolved foods, or
    // an explicit message (the all-fail case). On a normal success with nothing
    // unresolved, both are absent and we render no banner.
    if (list.length === 0 && !options.message) return;

    const notice = document.createElement('div');
    notice.className = 'nutrition-checker__unresolved-notice';
    notice.dataset.unresolvedNotice = '';
    notice.setAttribute('role', 'status');

    const title = document.createElement('strong');
    title.textContent = options.message || copyValue(this.copy, 'messages.unresolvedHeading', "We couldn't find nutrition data for:");
    notice.append(title);

    if (list.length > 0) {
      const items = document.createElement('ul');
      items.className = 'nutrition-checker__unresolved-list';
      list.forEach((food) => {
        const entry = document.createElement('li');
        const name = food.name || copyValue(this.copy, 'messages.unresolvedUnnamed', 'this item');
        entry.textContent = food.reason ? `${name} — ${food.reason}` : name;
        items.append(entry);
      });
      notice.append(items);
    }

    if (options.note) {
      const note = document.createElement('p');
      note.className = 'nutrition-checker__unresolved-note';
      note.textContent = options.note;
      notice.append(note);
    }

    this.query(`[data-nutrition-stage="${stage}"]`).prepend(notice);
  }

  renderTrackedNutrients() {
    const container = this.query('[data-tracked-nutrients]');
    container.innerHTML = '';

    Object.values(nutrients).forEach((definition) => {
      const item = document.createElement('div');
      item.className = 'nutrition-checker__tracked-item';
      item.textContent = `${definition.name} (${definition.unit})`;
      container.append(item);
    });
  }

  prefillMealTextFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const encoded = params.get('meals');
    if (!encoded) return false;

    try {
      const meals = JSON.parse(decodeURIComponent(encoded));
      let hasMeals = false;
      mealIds.forEach((mealId) => {
        if (!Array.isArray(meals[mealId]) || meals[mealId].length === 0) return;
        const textarea = this.query(`[data-meal-text="${mealId}"]`);
        textarea.value = meals[mealId].map((item) => item.name).join(', ');
        this.updateCreateButtonState(mealId);
        void this.createCardsForMeal(mealId);
        hasMeals = true;
      });
      return hasMeals;
    } catch (error) {
      console.warn('Could not parse meals URL parameter.', error);
      return false;
    }
  }

  reset() {
    this.root.querySelectorAll('[data-error-banner]').forEach((element) => element.remove());
    this.root.querySelectorAll('[data-unresolved-notice]').forEach((element) => element.remove());
    this.setStage('input');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  openDialog(selector) {
    const dialog = this.query(selector);
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
      return;
    }
    dialog.setAttribute('open', '');
  }

  closeDialog(selector) {
    const dialog = this.query(selector);
    if (typeof dialog.close === 'function') {
      dialog.close();
      return;
    }
    dialog.removeAttribute('open');
  }
}

function parseMealText(value) {
  return value
    .split(/\n|;|,|\s+(?:and|plus)\s+/i)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const serving = estimateServing(part);
      return {
        name: cleanFoodName(part, serving.phrase),
        servingPhrase: serving.phrase,
        servingGrams: serving.grams,
        confidence: serving.confidence
      };
    })
    .filter((item) => item.name.length > 0);
}

function estimateServing(text) {
  const explicitQuantity = text.match(/\b(\d+(?:\.\d+)?|one|two|three|half)\b/i);
  const servingMatch = servingEstimates.find((estimate) => estimate.pattern.test(text));
  const foodMatch = foodSpecificEstimates.find((estimate) => estimate.pattern.test(text));

  if (servingMatch) {
    const grams = adjustServingGrams(text, servingMatch.grams);
    const shouldScale = explicitQuantity && !/\bhalf\b/i.test(servingMatch.phrase);
    return {
      phrase: servingMatch.phrase,
      grams: shouldScale ? scaleServingGrams(grams, explicitQuantity[1]) : grams,
      confidence: explicitQuantity ? 'high' : servingMatch.confidence
    };
  }

  if (foodMatch) {
    return {
      phrase: foodMatch.phrase,
      grams: explicitQuantity
        ? scaleServingGrams(adjustServingGrams(text, foodMatch.grams), explicitQuantity[1])
        : adjustServingGrams(text, foodMatch.grams),
      confidence: explicitQuantity ? 'high' : foodMatch.confidence
    };
  }

  return { phrase: 'typical serving', grams: 120, confidence: 'low' };
}

function adjustServingGrams(text, grams) {
  if (/\b(toast|bread)\b/i.test(text) && /\b(peanut\s+butter|almond\s+butter)\b/i.test(text)) {
    return 85;
  }

  if (/\b(with\s+milk)\b/i.test(text)) {
    return grams + 30;
  }

  return grams;
}

function scaleServingGrams(grams, quantity) {
  const wordQuantities = { one: 1, two: 2, three: 3, half: 0.5 };
  const multiplier = wordQuantities[quantity.toLowerCase()] || Number.parseFloat(quantity);
  return Number.isFinite(multiplier) ? Math.round(grams * multiplier) : grams;
}

function cleanFoodName(text, servingPhrase) {
  return text
    .replace(new RegExp(`\\b${escapeRegExp(servingPhrase)}\\b`, 'i'), '')
    .replace(/\b(small|large)?\s*(bowl|cup|slice|piece|tablespoon|tbsp|teaspoon|tsp|handful|scoop|plate|serving)\b/gi, ' ')
    .replace(/\b(\d+(?:\.\d+)?|one|two|three|half)\b/i, '')
    .replace(/\b(of|a|an)\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isLikelyFoodMatch(query, description) {
  if (typeof description !== 'string') return false;
  const queryTokens = meaningfulFoodTokens(query);
  if (queryTokens.length === 0) return false;

  const normalizedDescription = normalizeFoodText(description);
  if (normalizedDescription.includes(normalizeFoodText(query))) return true;

  const descriptionTokens = new Set(meaningfulFoodTokens(description));
  const matchCount = queryTokens.filter((token) => descriptionTokens.has(token)).length;
  if (queryTokens.length === 1) return matchCount === 1;
  return matchCount / queryTokens.length >= 0.5;
}

function uniqueFoodSuggestions(foods) {
  const suggestions = [];
  foods.forEach((food) => {
    if (suggestions.length >= 3) return;
    if (typeof food.description !== 'string') return;
    if (suggestions.some((suggestion) => suggestion.description === food.description)) return;
    suggestions.push({
      description: food.description,
      fdcId: parseFdcId(food.fdcId)
    });
  });
  return suggestions;
}

function parseFdcId(value) {
  if (Number.isInteger(value)) return value;
  if (typeof value !== 'string' || !/^\d+$/.test(value)) return null;
  const fdcId = Number.parseInt(value, 10);
  return Number.isInteger(fdcId) ? fdcId : null;
}

function meaningfulFoodTokens(value) {
  return normalizeFoodText(value)
    .split(' ')
    .filter((token) => token.length > 1 && !foodMatchStopWords.has(token));
}

function normalizeFoodText(value) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function readCopy(root) {
  const script = root.querySelector('[data-nutrition-copy]');
  if (!script) return {};
  return JSON.parse(script.textContent);
}

function copyValue(copy, path, fallback) {
  const keys = Array.isArray(path) ? path : path.split('.');
  let value = copy;
  for (const key of keys) {
    if (!value || typeof value !== 'object' || !(key in value)) return fallback;
    value = value[key];
  }
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

function copyTemplate(copy, path, variables, fallback) {
  return Object.entries(variables).reduce((text, [key, value]) => {
    return text.replaceAll(`{${key}}`, String(value));
  }, copyValue(copy, path, fallback));
}

function mergeDefinitionLabels(definitions, labels = {}) {
  return Object.fromEntries(
    Object.entries(definitions).map(([key, definition]) => {
      return [key, { ...definition, name: labels[key] || definition.name }];
    })
  );
}

function buildBenefits(copy) {
  const benefits = copy.benefits || {};
  const order = Array.isArray(copy.benefitOrder) ? copy.benefitOrder : Object.keys(benefits);
  return order
    .filter((key) => benefits[key])
    .map((key) => {
      const benefit = benefits[key];
      return {
        key,
        name: benefit.name || '',
        color: benefit.color || '',
        description: benefit.description || '',
        detail: benefit.detail || '',
        nutrients: Array.isArray(benefit.nutrients) ? benefit.nutrients : []
      };
    });
}

function buildBeyond(copy) {
  const beyond = copy.beyond || {};
  const order = Array.isArray(copy.benefitOrder) ? copy.benefitOrder : Object.keys(beyond);
  return order
    .filter((key) => beyond[key])
    .map((key) => {
      const group = beyond[key];
      const items = Array.isArray(group.items) ? group.items.slice() : [];
      items.sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0));
      return {
        key,
        name: group.name || '',
        ingredients: items.map((item) => [item.name, item.desc])
      };
    });
}

function buildCore7Nutrients(copy) {
  const source = copy.core7Nutrients || {};
  const result = {};
  Object.keys(source).forEach((key) => {
    const nutrient = source[key];
    result[key] = {
      displayName: nutrient.displayName || key,
      amount: Number(nutrient.amount) || 0,
      unit: nutrient.unit || ''
    };
  });
  return result;
}

function transformAnalyzeResponse(data, meals) {
  return {
    analysisMode: 'quick',
    meals,
    score: data.score,
    scoreBand: data.score_band,
    totals: data.totals,
    macroTotals: data.macroTotals,
    benefitGaps: data.benefit_gaps,
    insights: data.insights,
    foodFirstSuggestions: data.food_first_suggestions,
    core7Fit: data.core7_fit,
    safetyFlags: data.safety_flags,
    disclaimer: data.disclaimer,
    supplements: [],
    supplementTotals: {},
    functionalCompounds: [],
    overages: [],
    costComparison: null,
    syntheticIngredientAudit: [],
    foods: (data.matched_foods || []).map((item) => ({
      query: item.query,
      servingPhrase: item.serving_phrase,
      servingGrams: item.serving_grams,
      confidence: item.confidence,
      portionNotes: item.portion_notes,
      food: {
        description: item.description,
        dataType: item.data_type
      }
    })),
    errors: []
  };
}

function transformFullStackResponse(data, meals) {
  return {
    analysisMode: 'full',
    meals,
    score: data.score,
    scoreBand: data.score_band,
    totals: data.totals,
    macroTotals: data.macroTotals,
    benefitGaps: data.benefit_gaps,
    insights: data.insights,
    foodFirstSuggestions: data.food_first_suggestions,
    core7Fit: data.core7_fit,
    safetyFlags: data.safety_flags,
    disclaimer: data.disclaimer,
    supplements: Array.isArray(data.matched_supplements) ? data.matched_supplements.map((item) => ({
      label_id: item.label_id,
      full_name: item.full_name,
      brand_name: item.brand_name,
      name: item.name,
      brand: item.brand,
      data_source: item.data_source,
      estimated_nutrients: item.estimated_nutrients,
      serving_summary: item.serving_summary,
      confidence: item.confidence
    })) : [],
    supplementTotals: data.supplement_totals,
    functionalCompounds: Array.isArray(data.functional_compounds) ? data.functional_compounds.map((item) => ({
      name: item.name,
      category: item.category,
      source: item.source,
      amount_summary: item.amount_summary,
      data_source: item.data_source,
      estimated_nutrients: item.estimated_nutrients
    })) : [],
    overages: Array.isArray(data.overages) ? data.overages.map((item) => ({
      name: item.name,
      amount: item.amount,
      unit: item.unit,
      limit: item.limit,
      percent_limit: item.percent_limit,
      source: item.source
    })) : [],
    costComparison: data.cost_comparison
      ? {
          monthly_supplement_spend: data.cost_comparison.monthly_supplement_spend,
          core7_monthly_price: data.cost_comparison.core7_monthly_price,
          monthly_difference: data.cost_comparison.monthly_difference,
          annual_difference: data.cost_comparison.annual_difference,
          summary: data.cost_comparison.summary
        }
      : null,
    syntheticIngredientAudit: Array.isArray(data.synthetic_ingredient_audit) ? data.synthetic_ingredient_audit.map((item) => ({
      supplement: item.supplement,
      ingredients: item.ingredients.map((ingredient) => ({
        name: ingredient.name,
        category: ingredient.category
      }))
    })) : [],
    foods: (data.matched_foods || []).map((item) => ({
      query: item.query,
      servingPhrase: item.serving_phrase,
      servingGrams: item.serving_grams,
      confidence: item.confidence,
      portionNotes: item.portion_notes,
      food: {
        description: item.description,
        dataType: item.data_type
      }
    })),
    errors: []
  };
}

function benefitInitial(name) {
  return String(name || '?').trim().charAt(0).toUpperCase();
}

function scoreBandFromCopy(score, copy) {
  return scoreBand(score, copy);
}

function scoreBand(score, copy) {
  if (score < 30) {
    return {
      label: copyValue(copy, 'score.needsAttention.label', 'Needs Attention'),
      message: copyValue(copy, 'score.needsAttention.message', "Your diet has significant nutritional gaps. CORE7 can help fill them and support your body's full potential.")
    };
  }
  if (score < 50) {
    return {
      label: copyValue(copy, 'score.belowAverage.label', 'Below Average'),
      message: copyValue(copy, 'score.belowAverage.message', 'Your diet is missing key micronutrients. CORE7 is designed to help bridge these gaps.')
    };
  }
  if (score < 70) {
    return {
      label: copyValue(copy, 'score.developing.label', 'Developing'),
      message: copyValue(copy, 'score.developing.message', 'You have a decent foundation, but notable gaps remain. CORE7 can help optimize your micronutrient balance.')
    };
  }
  if (score < 85) {
    return {
      label: copyValue(copy, 'score.good.label', 'Good'),
      message: copyValue(copy, 'score.good.message', 'Your diet is fairly well-rounded. CORE7 can help you maintain consistency on less-than-perfect days.')
    };
  }
  return {
    label: copyValue(copy, 'score.excellent.label', 'Excellent'),
    message: copyValue(copy, 'score.excellent.message', 'Great nutritional intake today. CORE7 can help you sustain this level consistently.')
  };
}

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function aggregateValues(collections, definitions) {
  const totals = {};
  Object.keys(definitions).forEach((key) => {
    totals[key] = collections.reduce((sum, collection) => sum + collection[key], 0);
  });
  return totals;
}

function subtractTotals(totals, subtractor, definitions) {
  const result = {};
  Object.keys(definitions).forEach((key) => {
    const total = Number.isFinite(totals?.[key]) ? totals[key] : 0;
    const amount = Number.isFinite(subtractor?.[key]) ? subtractor[key] : 0;
    result[key] = Math.max(0, total - amount);
  });
  return result;
}

function buildSampleReport() {
  const totals = {
    vitaminC: 35,
    vitaminA: 400,
    vitaminD: 3,
    vitaminE: 6,
    vitaminK: 70,
    thiamin: 0.8,
    riboflavin: 0.9,
    niacin: 10,
    pantoB5: 3,
    vitaminB6: 0.9,
    vitaminB12: 1.3,
    folate: 220,
    calcium: 600,
    iron: 6,
    magnesium: 170,
    phosphorus: 500,
    potassium: 1800,
    selenium: 35,
    zinc: 5.3,
    fiber: 14
  };

  return {
    analysisMode: 'quick',
    meals: [],
    score: calculateScore(totals),
    scoreBand: null,
    totals,
    macroTotals: { calories: 1500, protein: 55, carbohydrates: 180, fat: 55, fiber: 14 },
    benefitGaps: null,
    insights: {
      summary: 'Sample preview data. Several micronutrients are below target, leaving room for CORE7 to help close the gaps.'
    },
    foodFirstSuggestions: [
      'Add leafy greens for folate and magnesium',
      'Include citrus or berries for vitamin C'
    ],
    core7Fit: ['CORE7 adds whole-food vitamin C, D, magnesium, and zinc'],
    safetyFlags: [],
    disclaimer: '',
    supplements: [],
    supplementTotals: {},
    functionalCompounds: [],
    overages: [],
    costComparison: null,
    syntheticIngredientAudit: [],
    foods: [
      {
        query: 'Grilled chicken salad',
        servingPhrase: '1 large bowl',
        servingGrams: 320,
        confidence: 'high',
        portionNotes: '',
        food: { description: 'Salad, chicken, grilled', dataType: 'Survey (FNDDS)' }
      },
      {
        query: 'Brown rice',
        servingPhrase: '1 cup cooked',
        servingGrams: 195,
        confidence: 'medium',
        portionNotes: '',
        food: { description: 'Rice, brown, cooked', dataType: 'SR Legacy' }
      }
    ],
    errors: []
  };
}

function calculateScore(totals) {
  const fractions = Object.entries(nutrients).map(([key, definition]) => {
    const amount = Number.isFinite(totals?.[key]) ? totals[key] : 0;
    return Math.min(amount / definition.rda, 1);
  });
  return Math.round((fractions.reduce((sum, value) => sum + value, 0) / fractions.length) * 100);
}

function scoreLevel(percent) {
  return statusLevels.find((level) => percent < level.max);
}

function groupDefinitions(definitions) {
  return Object.entries(definitions).reduce((groups, entry) => {
    const group = entry[1].group;
    if (!groups[group]) groups[group] = [];
    groups[group].push(entry);
    return groups;
  }, {});
}

function formatAmount(value) {
  if (value === 0) return '0';
  if (value < 0.01) return value.toFixed(4);
  if (value < 0.1) return value.toFixed(3);
  if (value < 10) return value.toFixed(1);
  return String(Math.round(value));
}

function parseMoneyValue(value) {
  const amount = Number.parseFloat(String(value || '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(amount) ? amount : 0;
}

function formatCurrency(value) {
  const amount = Number.isFinite(value) ? value : 0;
  const sign = amount < 0 ? '-' : '';
  return `${sign}$${Math.abs(amount).toFixed(2)}`;
}

function formatCurrencyCompact(value) {
  const amount = Number.isFinite(value) ? value : 0;
  const sign = amount < 0 ? '-' : '';
  const absolute = Math.abs(amount);
  const decimals = Number.isInteger(absolute) ? 0 : 2;
  return `${sign}$${absolute.toFixed(decimals)}`;
}

function formatCurrencyRounded(value) {
  const amount = Number.isFinite(value) ? value : 0;
  return `$${Math.round(Math.abs(amount))}`;
}

function costDifferenceSummary(monthlyDifference) {
  const replacementCopy = 'replaces synthetic isolates with 51+ whole-food ingredients including adaptogens, probiotics, and phytonutrients no supplement pill can provide.';
  if (monthlyDifference > 0) {
    return `Core7 costs ${formatCurrencyRounded(monthlyDifference)} more per month than your current stack — but ${replacementCopy}`;
  }
  if (monthlyDifference < 0) {
    return `Core7 saves you ${formatCurrencyRounded(monthlyDifference)} per month compared with your current stack — and ${replacementCopy}`;
  }
  return `Core7 costs the same per month as your current stack — but ${replacementCopy}`;
}

function supplementDisplayNames(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => {
      if (!item || typeof item !== 'object') return '';
      return item.name || item.full_name || item.brand_name || item.brand || '';
    })
    .map((name) => String(name).trim())
    .filter(Boolean)
    .filter((name, index, names) => names.indexOf(name) === index);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNutritionCheckers);
} else {
  initNutritionCheckers();
}

document.addEventListener('shopify:section:load', initNutritionCheckers);
